import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Card, Table, Button, Modal, Form, Input, Select, AutoComplete, message, Tag,
  Row, Col, Statistic, Icon, Alert, Progress, Collapse, Tooltip, Spin, Checkbox
} from 'antd';
import {
  reclassLoginWithToken, reclassProcessCombined, reclassStop
} from './utils/reclassificationService';
import { getToken, getUserData } from './utils/authService';
import { getServerType, getServerDomain } from './utils/serverConfig';
import { apiGet } from './utils/apiService';
import { executeApplyChanges, repairAssetSubTypes, PHASE_LABELS } from './utils/applyChangesEngine';
import { buildReclassificationPrompts, parseReclassificationJson } from './utils/reclassificationPrompt';
import { decodeEntityNames } from './utils/htmlDecode';
import ClassificationMindMap from './ClassificationMindMap';

const { Option } = Select;
const { Panel } = Collapse;
const { Search } = Input;

const RECLASS_STORAGE_KEY = 'reclass_results';

function saveReclassificationResults(orgName, models, taxonomy, timingData) {
  const payload = {
    orgName,
    models,
    taxonomy,
    timingData,
    savedAt: new Date().toISOString(),
  };
  try {
    localStorage.setItem(RECLASS_STORAGE_KEY, JSON.stringify(payload));
  } catch (e) {
    console.error('Failed to save reclassification results:', e);
  }
}

function loadSavedResults() {
  try {
    const raw = localStorage.getItem(RECLASS_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (e) {
    console.error('Failed to load saved reclassification results:', e);
    return null;
  }
}

function clearSavedResults() {
  localStorage.removeItem(RECLASS_STORAGE_KEY);
}

// Helper: resolve current_category, current_subtype_code, current_model_no from AssetPro
// Fetches types, categories, subtypes, and models in parallel.
async function enrichModelsFromAssetPro(modelList) {
  try {
    const opts = { headers: { 'Accept': '*/*' } };
    const [typeRes, catRes, subRes, modelRes, makeRes] = await Promise.all([
      apiGet('/asset-type', opts),
      apiGet('/asset-classification', opts),
      apiGet('/equipment-sub-type', opts),
      apiGet('/asset-model', opts),
      apiGet('/asset-make', opts),
    ]);

    // Extract arrays from responses
    const extract = (res) => {
      if (!res.success) return [];
      const d = res.data;
      if (Array.isArray(d)) return d;
      if (d && Array.isArray(d.data)) return d.data;
      if (d && Array.isArray(d.subTypes)) return d.subTypes;
      if (d && Array.isArray(d.models)) return d.models;
      return [];
    };

    // Decode HTML entities (e.g. `&amp;` → `&`) on entity names so that all
    // downstream string comparisons and AutoComplete dropdowns see clean values.
    const types = decodeEntityNames(extract(typeRes));
    const categories = decodeEntityNames(extract(catRes));
    const subtypes = decodeEntityNames(extract(subRes));
    const assetModels = decodeEntityNames(extract(modelRes));
    const makes = decodeEntityNames(extract(makeRes));

    // Build category ID → name lookup
    const categoryById = {};
    categories.forEach(c => { categoryById[c._id] = c.name; });

    // Build type name (lowercase) → category name lookup
    const typeToCategoryName = {};
    types.forEach(t => {
      const catId = typeof t.assetClassification === 'string'
        ? t.assetClassification
        : t.assetClassification?._id;
      const catName = typeof t.assetClassification === 'object' && t.assetClassification?.name
        ? t.assetClassification.name
        : categoryById[catId] || '';
      if (t.name && catName) {
        typeToCategoryName[t.name.toLowerCase().trim()] = catName;
      }
    });

    // Build subtype name (lowercase) → code lookup
    const subtypeCodeByName = {};
    subtypes.forEach(s => {
      if (s.name && s.code) {
        subtypeCodeByName[s.name.toLowerCase().trim()] = s.code;
      }
    });

    // Build model _id → modelNumber lookup
    const modelNumberById = {};
    assetModels.forEach(am => {
      if (am._id && am.modelNumber) {
        modelNumberById[am._id] = am.modelNumber;
      }
    });

    // Build type ID → name lookup (for subtype → type resolution)
    const typeNameById = {};
    types.forEach(t => { typeNameById[t._id] = t.name; });

    // Build grouped entity lists for dropdown options
    // Categories: flat list
    const categoryNames = [...new Set(categories.map(c => c.name).filter(Boolean))].sort();

    // Types grouped by category name
    const typesByCategory = {};
    const allTypeNames = new Set();
    types.forEach(t => {
      if (!t.name) return;
      allTypeNames.add(t.name);
      const catId = typeof t.assetClassification === 'string'
        ? t.assetClassification : t.assetClassification?._id;
      const catName = typeof t.assetClassification === 'object' && t.assetClassification?.name
        ? t.assetClassification.name : categoryById[catId] || '';
      if (catName) {
        const key = catName.toLowerCase().trim();
        if (!typesByCategory[key]) typesByCategory[key] = new Set();
        typesByCategory[key].add(t.name);
      }
    });
    // Convert sets to sorted arrays
    Object.keys(typesByCategory).forEach(k => {
      typesByCategory[k] = [...typesByCategory[k]].sort();
    });

    // Subtypes grouped by type name
    const subtypesByType = {};
    const allSubtypeNames = new Set();
    subtypes.forEach(s => {
      if (!s.name) return;
      allSubtypeNames.add(s.name);
      const typeId = typeof s.assetType === 'string' ? s.assetType : s.assetType?._id;
      const typeName = typeof s.assetType === 'object' && s.assetType?.name
        ? s.assetType.name : typeNameById[typeId] || '';
      if (typeName) {
        const key = typeName.toLowerCase().trim();
        if (!subtypesByType[key]) subtypesByType[key] = new Set();
        subtypesByType[key].add(s.name);
      }
    });
    Object.keys(subtypesByType).forEach(k => {
      subtypesByType[k] = [...subtypesByType[k]].sort();
    });

    // Makes: flat list from /asset-make
    const makeNames = new Set();
    makes.forEach(mk => { if (mk.name) makeNames.add(mk.name); });

    // Build the entities object for dropdown options
    const entities = {
      categories: categoryNames,
      typesByCategory,
      allTypes: [...allTypeNames].sort(),
      subtypesByType,
      allSubtypes: [...allSubtypeNames].sort(),
      makes: [...makeNames].sort(),
    };

    // Enrich each model
    const enrichedModels = modelList.map(m => {
      const currentType = (m.current_type || '').toLowerCase().trim();
      const currentSubtype = (m.current_subtype || '').toLowerCase().trim();

      return {
        ...m,
        current_category: m.current_category || typeToCategoryName[currentType] || '',
        current_subtype_code: m.current_subtype_code || subtypeCodeByName[currentSubtype] || '',
        current_model_no: m.current_model_no || modelNumberById[m.model_id] || '',
      };
    });

    return { models: enrichedModels, entities };
  } catch (err) {
    console.warn('Failed to enrich models from AssetPro:', err.message);
    return { models: modelList, entities: null }; // Return unchanged if fetch fails
  }
}

// Helper: load models directly from the AssetPro API (no classification server needed).
// Fetches models, types, categories, subtypes, makes, and assets in parallel.
// Returns { models, entities } where entities is for dropdown options.
async function loadModelsFromAssetPro() {
  const opts = { headers: { 'Accept': '*/*' } };
  const [modelRes, typeRes, catRes, subRes, makeRes, assetRes, locRes, clientRes] = await Promise.all([
    apiGet('/asset-model', opts),
    apiGet('/asset-type', opts),
    apiGet('/asset-classification', opts),
    apiGet('/equipment-sub-type', opts),
    apiGet('/asset-make', opts),
    apiGet('/asset/asset-all', opts),
    apiGet('/location', opts),
    apiGet('/client', opts),
  ]);

  const extract = (res) => {
    if (!res.success) return [];
    const d = res.data;
    if (Array.isArray(d)) return d;
    if (d && Array.isArray(d.data)) return d.data;
    if (d && Array.isArray(d.subTypes)) return d.subTypes;
    if (d && Array.isArray(d.models)) return d.models;
    if (d && Array.isArray(d.assets)) return d.assets;
    return [];
  };

  // Decode HTML entities on entity names from AssetPro so downstream lookups,
  // AutoComplete options, and the apply engine all see clean strings.
  const assetModels = decodeEntityNames(extract(modelRes));
  const types = decodeEntityNames(extract(typeRes));
  const categories = decodeEntityNames(extract(catRes));
  const subtypes = decodeEntityNames(extract(subRes));
  const makes = decodeEntityNames(extract(makeRes));
  const assets = extract(assetRes);
  const locations = extract(locRes);
  const clients = extract(clientRes);

  // Build lookup maps by ID
  const categoryById = {};
  categories.forEach(c => { categoryById[c._id] = c; });

  const locationById = {};
  locations.forEach(l => { locationById[l._id] = l.name || l.code || ''; });

  const clientById = {};
  clients.forEach(c => { clientById[c._id] = c.name || c.code || ''; });

  const typeById = {};
  types.forEach(t => { typeById[t._id] = t; });

  const subtypeById = {};
  subtypes.forEach(s => { subtypeById[s._id] = s; });

  const makeById = {};
  makes.forEach(m => { makeById[m._id] = m; });

  // Count assets per model
  const assetCountByModel = {};
  assets.forEach(a => {
    const mId = typeof a.assetModel === 'string' ? a.assetModel : a.assetModel?._id;
    if (mId) assetCountByModel[mId] = (assetCountByModel[mId] || 0) + 1;
  });

  const refId = (ref) => {
    if (!ref) return '';
    if (typeof ref === 'string') return ref;
    return ref._id || '';
  };

  // Build model list with all current_* fields resolved
  const models = assetModels.map((am, i) => {
    // Resolve subtype
    const subId = refId(am.assetSubType);
    const sub = subtypeById[subId] || (typeof am.assetSubType === 'object' ? am.assetSubType : null);
    const subtypeName = sub?.name || '';
    const subtypeCode = sub?.code || '';

    // Resolve type (from subtype → assetType)
    const typeId = sub ? refId(sub.assetType) : '';
    const typ = typeById[typeId] || (typeof sub?.assetType === 'object' ? sub.assetType : null);
    const typeName = typ?.name || '';

    // Resolve category (from type → assetClassification)
    const catId = typ ? refId(typ.assetClassification) : '';
    const cat = categoryById[catId] || (typeof typ?.assetClassification === 'object' ? typ.assetClassification : null);
    const catName = cat?.name || '';

    // Resolve make
    const mkId = refId(am.assetMake);
    const mk = makeById[mkId] || (typeof am.assetMake === 'object' ? am.assetMake : null);
    const makeName = mk?.name || '';

    return {
      _index: i,
      model_id: am._id,
      current_model_name: am.name || '',
      current_category: catName,
      current_type: typeName,
      current_subtype: subtypeName,
      current_subtype_code: subtypeCode,
      current_make: makeName,
      current_model_number: am.modelNumber || '',
      current_model_no: am.modelNumber || '',
      asset_count: assetCountByModel[am._id] || 0,
      status: 'done',
      has_changes: false,
    };
  });

  // Build entities for dropdown options
  const categoryNames = [...new Set(categories.map(c => c.name).filter(Boolean))].sort();

  const typesByCategory = {};
  const allTypeNames = new Set();
  types.forEach(t => {
    if (!t.name) return;
    allTypeNames.add(t.name);
    const catId = refId(t.assetClassification);
    const catName = typeof t.assetClassification === 'object' && t.assetClassification?.name
      ? t.assetClassification.name : (categoryById[catId]?.name || '');
    if (catName) {
      const key = catName.toLowerCase().trim();
      if (!typesByCategory[key]) typesByCategory[key] = new Set();
      typesByCategory[key].add(t.name);
    }
  });
  Object.keys(typesByCategory).forEach(k => { typesByCategory[k] = [...typesByCategory[k]].sort(); });

  const subtypesByType = {};
  const allSubtypeNames = new Set();
  subtypes.forEach(s => {
    if (!s.name) return;
    allSubtypeNames.add(s.name);
    const typeId = refId(s.assetType);
    const typeName = typeof s.assetType === 'object' && s.assetType?.name
      ? s.assetType.name : (typeById[typeId]?.name || '');
    if (typeName) {
      const key = typeName.toLowerCase().trim();
      if (!subtypesByType[key]) subtypesByType[key] = new Set();
      subtypesByType[key].add(s.name);
    }
  });
  Object.keys(subtypesByType).forEach(k => { subtypesByType[k] = [...subtypesByType[k]].sort(); });

  const makeNames = [...new Set(makes.map(m => m.name).filter(Boolean))].sort();

  const entities = {
    categories: categoryNames,
    typesByCategory,
    allTypes: [...allTypeNames].sort(),
    subtypesByType,
    allSubtypes: [...allSubtypeNames].sort(),
    makes: makeNames,
  };

  // Build assets-by-model-id map with resolved fields for download
  const assetsByModelId = {};
  assets.forEach(a => {
    const mId = typeof a.assetModel === 'string' ? a.assetModel : a.assetModel?._id;
    if (!mId) return;
    if (!assetsByModelId[mId]) assetsByModelId[mId] = [];

    // Resolve location — may be populated object, string ID, or missing
    const loc = a.location;
    let locName = '';
    if (typeof loc === 'object' && loc) {
      locName = loc.name || loc.code || '';
    } else if (typeof loc === 'string' && loc) {
      locName = locationById[loc] || '';
    }

    // Resolve sub-location (client field) — same pattern
    const cli = a.client;
    let subLocName = '';
    if (typeof cli === 'object' && cli) {
      subLocName = cli.name || cli.code || '';
    } else if (typeof cli === 'string' && cli) {
      subLocName = clientById[cli] || '';
    }

    assetsByModelId[mId].push({
      assetCode: a.assetCode || '',
      altName: a.altName || '',
      ownerCode: a.ownerCode || '',
      location: locName,
      subLocation: subLocName,
    });
  });

  return { models, entities, assetsByModelId };
}

// Helper: generate subtype code from name (module-level so it can be used in defaults + component)
function generateSubTypeCode(name) {
  if (!name) return '';
  const words = name.toUpperCase().replace(/[^A-Z0-9\s]/g, '').trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '';
  if (words.length === 1) return words[0].substring(0, 3).padEnd(3, 'X');
  if (words.length === 2) return (words[0].substring(0, 2) + words[1][0]).substring(0, 3);
  return words.slice(0, 3).map(w => w[0]).join('').substring(0, 3);
}

// Helper: merge AI-proposed classifications into the model list.
//
// Shared by BOTH result routes — the JSON paste and the CSV upload — so the two
// can never drift apart. Entries are normalised before they get here:
//   { model_id, current_model_name, new_category?, new_type?, new_subtype?,
//     new_subtype_code?, new_make?, new_model_no? }
// A field that is absent or empty means "no proposal" and leaves the current
// value alone. `new_model_no` is the exception: when the key is present an empty
// string clears the value, because a model genuinely may have no part number.
//
// Matching is EXACT: model_id first, then an exact (case-insensitive) model name.
// There is no fuzzy matching — an entry that matches neither is counted in
// `notFound` and reported to the operator, never applied to a similar model.
//
// Returns { updated, updatedCount, notFoundCount, unchangedCount }.
function mergeClassificationEntries(models, entries) {
  const byId = {};
  const byName = {};
  models.forEach((m, i) => {
    if (m.model_id) byId[String(m.model_id)] = i;
    if (m.current_model_name) byName[m.current_model_name.toLowerCase()] = i;
  });

  const updated = [...models];
  let updatedCount = 0;
  let notFoundCount = 0;
  let unchangedCount = 0;

  for (const entry of entries) {
    const entryId = (entry.model_id || '').trim();
    const entryName = (entry.current_model_name || '').trim();

    let idx = -1;
    if (entryId && byId[entryId] !== undefined) idx = byId[entryId];
    else if (entryName && byName[entryName.toLowerCase()] !== undefined) idx = byName[entryName.toLowerCase()];

    if (idx < 0) {
      notFoundCount++;
      continue;
    }

    const m = { ...updated[idx] };
    let changed = false;

    if (entry.new_category) {
      if (entry.new_category !== m.new_category) { m.new_category = entry.new_category; changed = true; }
    }
    if (entry.new_type) {
      if (entry.new_type !== m.new_type) { m.new_type = entry.new_type; changed = true; }
    }
    if (entry.new_subtype && entry.new_subtype !== m.new_subtype) {
      m.new_subtype = entry.new_subtype;
      // Derive the code ourselves unless the source explicitly supplied one.
      // The AI is never asked for a code, so this is the normal path.
      if (!entry.new_subtype_code) m.new_subtype_code = generateSubTypeCode(entry.new_subtype);
      changed = true;
    }
    if (entry.new_subtype_code) {
      const code = entry.new_subtype_code.toUpperCase().replace(/[^A-Z0-9]/g, '').substring(0, 3);
      if (code && code !== m.new_subtype_code) { m.new_subtype_code = code; changed = true; }
    }
    if (entry.new_make) {
      if (entry.new_make !== m.new_make) { m.new_make = entry.new_make; changed = true; }
    }
    if (entry.new_model_no !== undefined) {
      if (entry.new_model_no !== m.new_model_no) { m.new_model_no = entry.new_model_no; changed = true; }
    }

    if (!changed) {
      unchangedCount++;
      continue;
    }

    const parts = [m.new_make || 'Generic'];
    if (m.new_subtype) parts.push(m.new_subtype);
    if (m.new_model_no) parts.push(m.new_model_no);
    m.new_asset_model = parts.join(' ');

    m.has_changes = (
      (m.new_type || '').toLowerCase() !== (m.current_type || '').toLowerCase() ||
      (m.new_subtype || '').toLowerCase() !== (m.current_subtype || '').toLowerCase() ||
      (m.new_make || '').toLowerCase() !== (m.current_make || '').toLowerCase()
    );
    m._user_edited = true;
    m.status = 'done';
    updated[idx] = m;
    updatedCount++;
  }

  return { updated, updatedCount, notFoundCount, unchangedCount };
}

// Helper: apply defaults so all "new_" fields mirror "current_" fields on initial load.
// SubType Code uses existing AssetPro code unless the subtype was changed.
// Asset Model is computed from formula: {Make} {SubType} {ModelNo}.
function applyModelDefaults(modelList) {
  return modelList.map((m, i) => {
    const newCategory = m.new_category || m.current_category || '';
    const newType = m.new_type || m.current_type || '';
    const newSubtype = m.new_subtype || m.current_subtype || '';
    const newMake = m.new_make || m.current_make || '';
    const newModelNo = m.new_model_no || m.current_model_number || m.current_model_no || m.model_no || m.model_number || m.modelNumber || '';

    // SubType Code logic:
    //  - If subtype hasn't changed, ALWAYS keep the existing AssetPro code
    //    (even if the server sent a generated one — existing codes may be manually set)
    //  - If subtype changed and user set a code, keep user's code
    //  - If subtype changed and no user code, auto-generate
    const subtypeChanged = newSubtype.toLowerCase().trim() !== (m.current_subtype || '').toLowerCase().trim();
    let newSubtypeCode;
    if (!subtypeChanged) {
      // Subtype unchanged — always preserve existing code
      newSubtypeCode = m.current_subtype_code || '';
    } else if (m._user_edited && m.new_subtype_code) {
      // User manually set a code — keep it
      newSubtypeCode = m.new_subtype_code;
    } else if (m.new_subtype_code) {
      // Server/AI generated code for a changed subtype — use it
      newSubtypeCode = m.new_subtype_code;
    } else if (newSubtype) {
      newSubtypeCode = generateSubTypeCode(newSubtype);
    } else {
      newSubtypeCode = '';
    }

    // Compute asset model name from formula: {Make} {SubType} {ModelNo}
    const parts = [];
    if (newMake) parts.push(newMake);
    if (newSubtype) parts.push(newSubtype);
    if (newModelNo) parts.push(newModelNo);
    const computedAssetModel = parts.length > 0 ? parts.join(' ') : '';
    const newAssetModel = computedAssetModel || m.new_asset_model || m.current_model_name || '';

    // Recalculate has_changes based on FINAL values (not server's pre-default values)
    // This ensures has_changes reflects actual differences after defaults are applied
    const hasChanges = (
      newCategory.toLowerCase() !== (m.current_category || '').toLowerCase() ||
      newType.toLowerCase() !== (m.current_type || '').toLowerCase() ||
      newSubtype.toLowerCase() !== (m.current_subtype || '').toLowerCase() ||
      newMake.toLowerCase() !== (m.current_make || '').toLowerCase() ||
      newSubtypeCode.toLowerCase() !== (m.current_subtype_code || '').toLowerCase()
    );

    return {
      ...m,
      _index: i !== undefined ? i : m._index,
      new_category: newCategory,
      new_type: newType,
      new_subtype: newSubtype,
      new_make: newMake,
      new_model_no: newModelNo,
      new_subtype_code: newSubtypeCode,
      new_asset_model: newAssetModel,
      has_changes: hasChanges,
    };
  });
}

// Helper: merge models with the same name (case-insensitive) into single rows.
// Combines asset counts and tracks all model IDs so the apply engine updates every duplicate.
function mergeCaseDuplicateModels(modelList) {
  const groups = {}; // lowercase name → array of models

  modelList.forEach(m => {
    const key = (m.current_model_name || '').toLowerCase().trim();
    if (!key) {
      // Models without a name can't be grouped — keep as separate entries
      const uid = '__unnamed_' + Math.random().toString(36).slice(2);
      groups[uid] = [m];
      return;
    }
    if (!groups[key]) groups[key] = [];
    groups[key].push(m);
  });

  const merged = [];
  for (const group of Object.values(groups)) {
    if (group.length === 1) {
      merged.push(group[0]);
    } else {
      // Multiple models with same name (different cases) — merge into one row
      group.sort((a, b) => (b.asset_count || 0) - (a.asset_count || 0));
      const canonical = { ...group[0] };
      canonical.asset_count = group.reduce((sum, m) => sum + (m.asset_count || 1), 0);
      canonical.merged_model_ids = group.map(m => m.model_id).filter(Boolean);
      canonical._merged_count = group.length;
      merged.push(canonical);
    }
  }

  return merged.map((m, i) => ({ ...m, _index: i }));
}

function AssetReclassification() {
  // Session state
  const [sessionId, setSessionId] = useState(null);
  const [orgName, setOrgName] = useState('');
  const [orgType, setOrgType] = useState('');
  const [initializing, setInitializing] = useState(true);
  const [initError, setInitError] = useState(null);

  // Models & classification
  const [models, setModels] = useState([]);
  const [assetsByModelId, setAssetsByModelId] = useState({});
  const [taxonomy, setTaxonomy] = useState([]);
  const [modelsLoading, setModelsLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [timingData, setTimingData] = useState(null);

  // Saved results
  const [savedAt, setSavedAt] = useState(null);

  // Apply changes state
  const [selectedForApply, setSelectedForApply] = useState({}); // { _index: true/false }
  const [applying, setApplying] = useState(false);
  const [payloadModalVisible, setPayloadModalVisible] = useState(false);
  const [payloadData, setPayloadData] = useState(null);
  const [applyProgressVisible, setApplyProgressVisible] = useState(false);
  const [applyProgress, setApplyProgress] = useState(null); // { phase, current, total, detail }
  const [applyResult, setApplyResult] = useState(null); // ExecutionLog
  const applyStartTimeRef = useRef(null);

  // Existing AssetPro entities for dropdown options
  const [existingEntities, setExistingEntities] = useState(null); // { categories, typesByCategory, allTypes, subtypesByType, allSubtypes, makes }

  // UI state
  const [filterText, setFilterText] = useState('');
  const [columnFilters, setColumnFilters] = useState({}); // { dataIndex: filterValue }
  const [mindmapVisible, setMindmapVisible] = useState(false);
  const [flowMapVisible, setFlowMapVisible] = useState(false);
  // Copy Prompt (offline route — no classification server, no API key)
  const [promptModalVisible, setPromptModalVisible] = useState(false);
  const [promptParts, setPromptParts] = useState([]); // [{ part, totalParts, from, to, count, format, text }]
  const [promptPartIndex, setPromptPartIndex] = useState(0);
  const [promptScopeLabel, setPromptScopeLabel] = useState('');
  const [promptFormat, setPromptFormat] = useState('json'); // 'json' (paste back) | 'csv' (file round trip)
  // Paste AI Result (JSON)
  const [pasteModalVisible, setPasteModalVisible] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const [pasteError, setPasteError] = useState(null);
  const [editingCell, setEditingCell] = useState(null); // { index, field }
  const [editValue, setEditValue] = useState(''); // controlled value for AutoComplete editing
  const editValueRef = useRef(''); // mirror of editValue — safe to read in stale closures (onBlur/Enter)
  const acCommittedRef = useRef(false); // prevents onBlur from double-committing after onSelect/Enter
  const searchInputRef = useRef(null); // ref for column filter search input
  const fileInputRef = useRef(null); // ref for CSV file upload input
  const orgTypeFetchedRef = useRef(false); // org-type lookup is attempted at most once per mount

  // ── Auto-initialize: load models from AssetPro API directly ──
  useEffect(() => {
    const initSession = async () => {
      const token = getToken();
      const userData = getUserData();

      if (!token) {
        setInitError('No authentication token found. Please log in to AIM first.');
        setInitializing(false);
        return;
      }

      // Extract organisation slug from userData
      const orgObj = userData?.organisation;
      const orgSlug = typeof orgObj === 'object' ? (orgObj?.slug || orgObj?.name || '') : (orgObj || '');
      setOrgName(orgSlug);

      // Check for saved results first
      const saved = loadSavedResults();
      if (saved && saved.models && saved.models.length > 0) {
        // Restore saved results
        let restoredModels = mergeCaseDuplicateModels(saved.models);
        restoredModels = applyModelDefaults(restoredModels);

        // Re-enrich from AssetPro (refresh current_* fields + dropdown entities)
        const enrichResult = await enrichModelsFromAssetPro(restoredModels);
        restoredModels = applyModelDefaults(enrichResult.models);
        if (enrichResult.entities) setExistingEntities(enrichResult.entities);

        setModels(restoredModels);
        setTaxonomy(saved.taxonomy || []);
        setTimingData(saved.timingData || null);
        setOrgName(saved.orgName || orgSlug);
        setSavedAt(saved.savedAt);
        message.success(`Restored ${restoredModels.length} models from last reclassification (${new Date(saved.savedAt).toLocaleString()})`);
        setInitializing(false);
        return;
      }

      // Fresh load: get models directly from AssetPro API
      setModelsLoading(true);
      try {
        const { models: loadedModels, entities, assetsByModelId: loadedAssets } = await loadModelsFromAssetPro();
        let loaded = mergeCaseDuplicateModels(loadedModels);
        loaded = applyModelDefaults(loaded);
        if (entities) setExistingEntities(entities);
        if (loadedAssets) setAssetsByModelId(loadedAssets);
        setModels(loaded);
        message.success(`Loaded ${loaded.length} unique asset models`);
      } catch (err) {
        setInitError('Failed to load models from AssetPro: ' + err.message);
      } finally {
        setModelsLoading(false);
        setInitializing(false);
      }
    };

    initSession();
  }, []);

  // ── Reclassification (only part that needs the classification server) ──
  const handleStartReclassification = async () => {
    // Use filtered models if filters are active, otherwise all models
    const hasFilters = filterText || Object.keys(columnFilters).length > 0;
    const modelsToClassify = hasFilters ? filteredModels : models;
    if (modelsToClassify.length === 0) return;
    setProcessing(true);
    setTimingData(null);
    try {
      // Create classification server session on-demand if we don't have one
      let sid = sessionId;
      if (!sid) {
        const token = getToken();
        const userData = getUserData();
        const orgObj = userData?.organisation;
        const orgSlug = typeof orgObj === 'object' ? (orgObj?.slug || orgObj?.name || '') : (orgObj || '');
        message.info('Connecting to classification server...');
        const loginResult = await reclassLoginWithToken(token, orgSlug);
        sid = loginResult.session_id;
        setSessionId(sid);
        if (loginResult.org_type) setOrgType(loginResult.org_type);
      }

      // Send only the visible/filtered models to the server
      const modelsForServer = modelsToClassify.map(m => ({
        model_id: m.model_id,
        current_model_name: m.current_model_name || '',
        current_type: m.current_type || '',
        current_subtype: m.current_subtype || '',
        current_make: m.current_make || '',
        current_category: m.current_category || '',
        current_subtype_code: m.current_subtype_code || '',
        current_model_number: m.current_model_number || '',
        asset_count: m.asset_count || 0,
        status: 'pending',
      }));

      const filterLabel = hasFilters ? ` (filtered ${modelsForServer.length}/${models.length})` : '';
      message.info(`Running OpenAI reclassification on ${modelsForServer.length} models${filterLabel}...`);
      const result = await reclassProcessCombined(sid, 'openai', modelsForServer);

      const tax = result.taxonomy || [];
      setTaxonomy(tax);

      const aiModels = result.models || [];
      console.log(`[Reclass] Server returned ${aiModels.length} classified models.`,
        aiModels.length > 0 ? 'Sample:' : '(empty)',
        aiModels.length > 0 ? JSON.stringify({ keys: Object.keys(aiModels[0]), model_id: aiModels[0].model_id, name: aiModels[0].current_model_name, status: aiModels[0].status }) : '');

      // Build lookups by multiple possible ID fields AND by name (case-insensitive)
      const aiByModelId = {};
      const aiByName = {};
      aiModels.forEach(m => {
        const id = m.model_id || m._id || m.id;
        if (id) aiByModelId[id] = m;
        const name = (m.current_model_name || m.name || '').toLowerCase().trim();
        if (name) aiByName[name] = m;
      });

      console.log(`[Reclass] AI lookup: ${Object.keys(aiByModelId).length} by ID, ${Object.keys(aiByName).length} by name`);

      // Use functional setModels to merge against the LATEST state (avoids stale closure)
      let mergeCount = 0;
      let classified;
      setModels(currentModels => {
        console.log(`[Reclass] Merging into ${currentModels.length} current models (closure had ${models.length})`);

        const merged = currentModels.map(existing => {
          // Try matching by model_id (multiple possible field names)
          let ai = aiByModelId[existing.model_id];

          // For merged rows (case-duplicates), check all merged_model_ids
          if (!ai && existing.merged_model_ids) {
            for (const mid of existing.merged_model_ids) {
              ai = aiByModelId[mid];
              if (ai) break;
            }
          }

          // Fall back to matching by current_model_name (case-insensitive)
          if (!ai) {
            const name = (existing.current_model_name || '').toLowerCase().trim();
            if (name) ai = aiByName[name];
          }

          if (!ai) return existing; // No AI suggestion — keep existing defaults

          mergeCount++;
          return {
            ...existing,
            new_category: ai.new_category || existing.new_category || '',
            new_type: ai.new_type || existing.new_type || '',
            new_subtype: ai.new_subtype || existing.new_subtype || '',
            new_make: ai.new_make || existing.new_make || '',
            new_model_no: ai.new_model_no || existing.new_model_no || '',
            new_subtype_code: ai.new_subtype_code || existing.new_subtype_code || '',
            has_changes: ai.has_changes !== undefined ? ai.has_changes : existing.has_changes,
            status: 'done',
          };
        });

        console.log(`[Reclass] Merged ${mergeCount}/${currentModels.length} models with AI suggestions`);

        // Fallback: if merge matched zero models, use server's classified models directly
        if (mergeCount === 0 && aiModels.length > 0) {
          console.warn(`[Reclass] Zero merge matches — falling back to server models directly`);
          classified = applyModelDefaults(mergeCaseDuplicateModels(aiModels));
          mergeCount = aiModels.length;
        } else {
          classified = applyModelDefaults(merged);
        }
        return classified;
      });

      const td = {
        startTime: result.start_time,
        endTime: result.end_time,
        duration: result.total_time_ms,
        processed: result.processed,
        errors: result.errors,
        stopped: result.stopped,
      };
      setTimingData(td);

      const changedCount = classified ? classified.filter(m => m.has_changes).length : 0;
      const unchangedCount = classified ? classified.filter(m => m.status === 'done' && !m.has_changes).length : 0;

      // Auto-select all rows that have changes proposed by AI
      if (classified) {
        const autoSelected = {};
        classified.forEach(m => {
          if (m.has_changes) autoSelected[m._index] = true;
        });
        setSelectedForApply(autoSelected);
      }

      // Persist results to localStorage
      if (classified) {
        saveReclassificationResults(orgName, classified, tax, td);
        setSavedAt(new Date().toISOString());
      }

      const totalModels = classified ? classified.length : models.length;
      message.success(
        `Reclassification complete: ${mergeCount}/${totalModels} classified, ${changedCount} changes, ${unchangedCount} no changes, ${result.errors} errors`
      );
    } catch (err) {
      message.error('Reclassification failed: ' + err.message);
    } finally {
      setProcessing(false);
    }
  };

  const handleStop = async () => {
    try {
      await reclassStop(sessionId);
      message.info('Stop requested — will halt after current batch');
    } catch (err) {
      message.error('Stop failed: ' + err.message);
    }
  };

  const handleReconnect = async () => {
    setInitError(null);
    setSavedAt(null);
    setModelsLoading(true);
    try {
      const { models: loadedModels, entities, assetsByModelId: loadedAssets } = await loadModelsFromAssetPro();
      let loaded = mergeCaseDuplicateModels(loadedModels);
      loaded = applyModelDefaults(loaded);
      if (entities) setExistingEntities(entities);
      if (loadedAssets) setAssetsByModelId(loadedAssets);
      setModels(loaded);
      message.success(`Loaded ${loaded.length} unique asset models`);
    } catch (err) {
      message.error('Failed to load models: ' + err.message);
    } finally {
      setModelsLoading(false);
    }
  };

  // ── SubType code generator ──
  // ── Cell editing ──
  const updateModelField = (index, field, value) => {
    setModels(prev => {
      const updated = [...prev];
      const m = { ...updated[index] };
      m[field] = value;
      m._user_edited = true;

      // Auto-generate subtype code when subtype changes
      if (field === 'new_subtype') {
        m.new_subtype_code = generateSubTypeCode(value);
      }

      // Recalculate asset model
      const parts = [m.new_make || 'Generic'];
      if (m.new_subtype) parts.push(m.new_subtype);
      if (m.new_model_no) parts.push(m.new_model_no);
      m.new_asset_model = parts.join(' ');

      // Recalculate has_changes
      m.has_changes = (
        (m.new_category || '').toLowerCase() !== (m.current_category || '').toLowerCase() ||
        (m.new_type || '').toLowerCase() !== (m.current_type || '').toLowerCase() ||
        (m.new_subtype || '').toLowerCase() !== (m.current_subtype || '').toLowerCase() ||
        (m.new_make || '').toLowerCase() !== (m.current_make || '').toLowerCase() ||
        (m.new_subtype_code || '').toLowerCase() !== (m.current_subtype_code || '').toLowerCase()
      );
      m.status = 'done';

      updated[index] = m;

      // Persist edits to localStorage
      saveReclassificationResults(orgName, updated, taxonomy, timingData);

      return updated;
    });
    setEditingCell(null);
  };

  // ── Dropdown options: merge existing AssetPro entities + taxonomy + model values ──
  const getFieldOptions = (field, model) => {
    if (field === 'new_category') {
      const fromTaxonomy = taxonomy.map(t => t.classification).filter(Boolean);
      const fromAssetPro = existingEntities?.categories || [];
      const fromModels = models.map(m => m.new_category).filter(Boolean);
      return [...new Set([...fromAssetPro, ...fromTaxonomy, ...fromModels])].sort();
    }
    if (field === 'new_type') {
      const curClass = (model.new_category || '').toLowerCase().trim();
      const fromTaxonomy = taxonomy
        .filter(t => !curClass || (t.classification || '').toLowerCase().trim() === curClass)
        .map(t => t.type).filter(Boolean);
      const fromAssetPro = curClass && existingEntities?.typesByCategory?.[curClass]
        ? existingEntities.typesByCategory[curClass]
        : existingEntities?.allTypes || [];
      const fromModels = models
        .filter(m => m.new_type && (!curClass || (m.new_category || '').toLowerCase().trim() === curClass))
        .map(m => m.new_type);
      return [...new Set([...fromAssetPro, ...fromTaxonomy, ...fromModels])].sort();
    }
    if (field === 'new_subtype') {
      const curType = (model.new_type || '').toLowerCase().trim();
      const fromAssetPro = curType && existingEntities?.subtypesByType?.[curType]
        ? existingEntities.subtypesByType[curType]
        : existingEntities?.allSubtypes || [];
      const fromModels = models
        .filter(m => m.new_subtype && (!curType || (m.new_type || '').toLowerCase().trim() === curType))
        .map(m => m.new_subtype);
      return [...new Set([...fromAssetPro, ...fromModels])].sort();
    }
    if (field === 'new_make') {
      const fromAssetPro = existingEntities?.makes || [];
      const fromModels = models.filter(m => m.new_make).map(m => m.new_make);
      return [...new Set([...fromAssetPro, ...fromModels])].sort();
    }
    return [];
  };

  // ── Field-level change detection ──
  const CURRENT_FIELD_MAP = {
    new_category: 'current_category',
    new_type: 'current_type',
    new_subtype: 'current_subtype',
    new_make: 'current_make',
    new_model_no: 'current_model_number',
  };

  const isFieldChanged = (record, field) => {
    const currentField = CURRENT_FIELD_MAP[field];
    if (!currentField) return false;
    const newVal = (record[field] || '').toLowerCase().trim();
    const curVal = (record[currentField] || '').toLowerCase().trim();
    return newVal !== curVal;
  };

  // ── Editable cell renderer ──
  const renderEditableCell = (text, record, field) => {
    const index = record._index;
    const isEditing = editingCell && editingCell.index === index && editingCell.field === field;

    if (isEditing) {
      if (field === 'new_model_no') {
        return (
          <Input
            size="small"
            defaultValue={text || ''}
            autoFocus
            onPressEnter={(e) => updateModelField(index, field, e.target.value)}
            onBlur={(e) => updateModelField(index, field, e.target.value)}
            style={{ width: '100%' }}
          />
        );
      }

      const options = getFieldOptions(field, record);
      return (
        <AutoComplete
          size="small"
          value={editValue}
          placeholder="Type to search or enter custom..."
          style={{ width: '100%', minWidth: 120 }}
          autoFocus
          defaultOpen
          dataSource={options}
          filterOption={(input, option) =>
            option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
          }
          onSelect={(val) => {
            acCommittedRef.current = true;
            updateModelField(index, field, val);
          }}
          onChange={(val) => {
            editValueRef.current = val;
            setEditValue(val);
          }}
          onBlur={() => {
            setTimeout(() => {
              if (acCommittedRef.current) {
                acCommittedRef.current = false;
                return;
              }
              const val = editValueRef.current;
              if (val && val !== (text || '')) {
                updateModelField(index, field, val);
              } else {
                setEditingCell(null);
              }
            }, 250);
          }}
          onInputKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              e.stopPropagation();
              const val = editValueRef.current;
              if (val) {
                acCommittedRef.current = true;
                updateModelField(index, field, val);
              }
            } else if (e.key === 'Escape') {
              acCommittedRef.current = true;
              setEditingCell(null);
            }
          }}
        />
      );
    }

    const isEdited = record._user_edited;
    const fieldChanged = isFieldChanged(record, field);
    return (
      <span
        onClick={() => {
          const val = record[field] || '';
          editValueRef.current = val;
          setEditValue(val);
          acCommittedRef.current = false;
          setEditingCell({ index, field });
        }}
        style={{
          cursor: 'pointer',
          color: text
            ? (fieldChanged ? (isEdited ? '#d48806' : '#389e0d') : '#888')
            : '#999',
          minWidth: 60,
          display: 'inline-block',
          fontWeight: fieldChanged ? 600 : (text ? 500 : 400),
          fontStyle: text ? 'normal' : 'italic',
          background: fieldChanged ? (isEdited ? '#fff7e6' : '#f6ffed') : 'transparent',
          padding: fieldChanged ? '1px 4px' : 0,
          borderRadius: fieldChanged ? 3 : 0,
          borderLeft: fieldChanged ? `2px solid ${isEdited ? '#faad14' : '#52c41a'}` : 'none',
        }}
      >
        {text || '— click to edit'}
      </span>
    );
  };

  // ── Apply Changes helpers ──
  const selectedCount = useMemo(() => {
    return Object.values(selectedForApply).filter(Boolean).length;
  }, [selectedForApply]);

  const selectableModels = useMemo(() => {
    return models.filter(m => m.status === 'done' && (m.has_changes || m._user_edited));
  }, [models]);

  const toggleApply = (index) => {
    setSelectedForApply(prev => ({ ...prev, [index]: !prev[index] }));
  };

  const toggleAllApply = (checked) => {
    const next = {};
    if (checked) {
      // Only select models visible after filters that have actual changes
      filteredModels.forEach(m => {
        if (m.status === 'done' && (m.has_changes || m._user_edited)) next[m._index] = true;
      });
    }
    setSelectedForApply(next);
  };

  const handleApplyChanges = () => {
    const rowsToApply = models.filter(m => selectedForApply[m._index]);
    if (rowsToApply.length === 0) {
      message.warning('No rows selected for applying changes');
      return;
    }

    // Build the payload that will be sent to the AssetPro API
    // Expand merged models (case-duplicates) into individual change entries
    const expandedChanges = rowsToApply.flatMap(m => {
      const ids = m.merged_model_ids && m.merged_model_ids.length > 1
        ? m.merged_model_ids
        : [m.model_id];
      return ids.map(mid => ({
        model_id: mid,
        current_model_name: m.current_model_name || '',
        current_type: m.current_type || '',
        current_subtype: m.current_subtype || '',
        current_make: m.current_make || '',
        new_category: m.new_category || '',
        new_type: m.new_type || '',
        new_subtype: m.new_subtype || '',
        new_subtype_code: m.new_subtype_code || '',
        new_make: m.new_make || '',
        new_model_no: m.new_model_no || '',
        new_asset_model: m.new_asset_model || '',
        has_changes: !!m.has_changes,
        asset_count: m.asset_count || 1,
      }));
    });

    const payload = {
      organisation: orgName,
      applied_at: new Date().toISOString(),
      total_models: expandedChanges.length,
      changes: expandedChanges,
    };

    setPayloadData(payload);
    setPayloadModalVisible(true);
  };

  const handleConfirmApply = async () => {
    setPayloadModalVisible(false);
    setApplyProgressVisible(true);
    setApplyResult(null);
    setApplying(true);
    applyStartTimeRef.current = Date.now();

    try {
      const result = await executeApplyChanges(
        payloadData.changes,
        (phase, current, total, detail) => {
          setApplyProgress({ phase, current, total, detail });
        }
      );

      // Update model rows with applied/error status
      setModels(prev => {
        const updated = [...prev];
        const updatedModelIds = new Set(result.phases.models.updated.map(m => m.modelId));
        const reassignedModelIds = new Set((result.phases.models.reassigned || []).map(m => m.modelId));
        const errorModelIds = new Set(result.phases.models.errors.map(m => m.modelId));

        for (const idx of Object.keys(selectedForApply)) {
          if (selectedForApply[idx] && updated[idx]) {
            const modelId = updated[idx].model_id;
            if (updatedModelIds.has(modelId) || reassignedModelIds.has(modelId)) {
              updated[idx] = { ...updated[idx], _applied: true };
            } else if (errorModelIds.has(modelId)) {
              updated[idx] = { ...updated[idx], _applyError: true };
            }
          }
        }
        saveReclassificationResults(orgName, updated, taxonomy, timingData);
        return updated;
      });

      result._totalChanges = payloadData.changes.length;
      setApplyResult(result);
      setSelectedForApply({});

      const totalErrors = Object.values(result.phases).reduce((sum, p) => sum + (p.errors?.length || 0), 0);
      if (totalErrors === 0) {
        message.success(`All ${payloadData.changes.length} model(s) applied successfully!`);
      } else {
        message.warning(`Applied with ${totalErrors} error(s). Check the results for details.`);
      }
    } catch (err) {
      message.error('Apply changes failed: ' + err.message);
    } finally {
      setApplying(false);
      setPayloadData(null);
    }
  };

  const handleRepairAssetSubTypes = async () => {
    Modal.confirm({
      title: 'Repair Asset SubTypes',
      content: 'This will scan all assets and fix any whose equipment sub-type doesn\'t match their model\'s sub-type. No AI reclassification is needed. Continue?',
      okText: 'Run Repair',
      okType: 'primary',
      icon: <Icon type="tool" />,
      onOk: async () => {
        setApplyProgressVisible(true);
        setApplyResult(null);
        setApplying(true);
        applyStartTimeRef.current = Date.now();

        try {
          const result = await repairAssetSubTypes(
            (phase, current, total, detail) => {
              setApplyProgress({ phase, current, total, detail });
            }
          );

          // Convert repair log to applyResult-compatible shape for the modal
          const asResult = {
            ...result,
            phases: {
              categories: { created: [], renamed: [], reused: [], errors: [] },
              types: { created: [], renamed: [], reused: [], errors: [] },
              subtypes: { created: [], renamed: [], reused: [], errors: [] },
              makes: { created: [], renamed: [], reused: [], errors: [] },
              models: { updated: [], reassigned: [], skipped: [], errors: [] },
              assets: { reassigned: [], skipped: [], errors: [] },
              cleanup: { deleted: [], errors: [] },
              verify: {
                passed: [],
                failed: [],
                assetsPassed: result.fixed.map(f => ({
                  assetId: f.assetId, assetCode: f.assetCode, model: f.model,
                })),
                assetsFailed: result.errors.map(e => ({
                  assetId: e.assetId, assetCode: e.assetCode, model: e.model, issue: e.error,
                })),
                errors: [],
              },
              repair: {
                fixed: result.fixed,
                alreadyCorrect: result.alreadyCorrect,
                noModelSubType: result.noModelSubType,
                errors: result.errors,
              },
            },
            _totalChanges: result.fixed.length,
          };

          setApplyResult(asResult);

          if (result.fixed.length > 0) {
            message.success(`Repaired ${result.fixed.length} asset(s). ${result.alreadyCorrect} were already correct.`);
          } else {
            message.info(`All assets already have correct sub-types. Nothing to fix.`);
          }
        } catch (err) {
          message.error('Repair failed: ' + err.message);
        } finally {
          setApplying(false);
        }
      },
    });
  };

  // ── Precompute distinct values for all filterable columns ──
  const EMPTY_SENTINEL = '__empty__';
  const distinctValues = useMemo(() => {
    const fields = [
      'current_model_name', 'current_category', 'current_type', 'current_subtype',
      'current_subtype_code', 'current_make', 'current_model_number',
      'new_category', 'new_type', 'new_subtype', 'new_subtype_code', 'new_make',
      'new_model_no', 'new_asset_model',
    ];
    const result = {};
    for (const f of fields) {
      const vals = new Set();
      let hasEmpty = false;
      models.forEach(m => {
        if (m[f]) vals.add(m[f]);
        else hasEmpty = true;
      });
      const sorted = [...vals].sort();
      if (hasEmpty) sorted.unshift(EMPTY_SENTINEL);
      result[f] = sorted;
    }
    return result;
  }, [models]);

  // ── Searchable multi-select column filter for Ant Design v3 ──
  const getColumnFilterProps = (dataIndex, title) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => {
      // Store search text in a special key prefix to separate it from actual filter values
      const rawKeys = selectedKeys || [];
      const searchKey = rawKeys.find(k => typeof k === 'string' && k.startsWith('__search:'));
      const searchText = searchKey ? searchKey.slice(9) : '';
      const checked = rawKeys.filter(k => typeof k === 'string' && !k.startsWith('__search:'));
      const allVals = distinctValues[dataIndex] || [];
      const filteredVals = searchText
        ? allVals.filter(v => v === EMPTY_SENTINEL
            ? '(empty)'.includes(searchText.toLowerCase())
            : v.toLowerCase().includes(searchText.toLowerCase()))
        : allVals;

      const updateKeys = (newChecked, newSearch) => {
        const next = [...newChecked];
        if (newSearch) next.push('__search:' + newSearch);
        setSelectedKeys(next);
      };

      return (
        <div style={{ padding: 8, width: 260 }}>
          <Input
            ref={searchInputRef}
            placeholder={`Search ${title}...`}
            value={searchText}
            onChange={e => updateKeys(checked, e.target.value)}
            style={{ marginBottom: 8, display: 'block' }}
            allowClear
            prefix={<Icon type="search" style={{ color: '#bbb' }} />}
          />
          <div style={{ maxHeight: 220, overflow: 'auto', marginBottom: 8, borderTop: '1px solid #f0f0f0', paddingTop: 4 }}>
            {filteredVals.length === 0 && (
              <div style={{ color: '#bbb', padding: '8px 0', textAlign: 'center', fontSize: 12 }}>No matches</div>
            )}
            {filteredVals.map(val => (
              <div key={val} style={{ padding: '3px 0' }}>
                <Checkbox
                  checked={checked.includes(val)}
                  onChange={e => {
                    const newChecked = e.target.checked
                      ? [...checked, val]
                      : checked.filter(v => v !== val);
                    updateKeys(newChecked, searchText);
                  }}
                >
                  <span style={{ fontSize: 12, color: val === EMPTY_SENTINEL ? '#bbb' : undefined, fontStyle: val === EMPTY_SENTINEL ? 'italic' : undefined }}>
                    {val === EMPTY_SENTINEL ? '(Empty)' : val}
                  </span>
                </Checkbox>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid #f0f0f0', paddingTop: 8 }}>
            <Button
              size="small"
              type="link"
              disabled={checked.length === 0}
              onClick={() => updateKeys([], searchText)}
            >
              Deselect All
            </Button>
            <div style={{ display: 'flex', gap: 4 }}>
              <Button
                size="small"
                onClick={() => {
                  clearFilters();
                  setColumnFilters(prev => {
                    const n = { ...prev };
                    delete n[dataIndex];
                    return n;
                  });
                }}
              >
                Reset
              </Button>
              <Button
                type="primary"
                size="small"
                icon="filter"
                onClick={() => {
                  // Set only the real filter values (strip __search: marker) before confirming
                  setSelectedKeys(checked);
                  confirm();
                  if (checked.length > 0) {
                    setColumnFilters(prev => ({ ...prev, [dataIndex]: checked }));
                  } else {
                    setColumnFilters(prev => {
                      const n = { ...prev };
                      delete n[dataIndex];
                      return n;
                    });
                  }
                }}
              >
                OK{checked.length > 0 ? ` (${checked.length})` : ''}
              </Button>
            </div>
          </div>
        </div>
      );
    },
    filterIcon: filtered => (
      <Icon type="filter" theme={filtered ? 'filled' : 'outlined'} style={{ color: filtered ? '#1890ff' : undefined }} />
    ),
    onFilter: (value, record) => {
      if (value === EMPTY_SENTINEL) return !record[dataIndex];
      return (record[dataIndex] || '') === value;
    },
    onFilterDropdownVisibleChange: visible => {
      if (visible) {
        setTimeout(() => searchInputRef.current && searchInputRef.current.select(), 100);
      }
    },
  });

  // ── Filtered models (applies both quick-search AND column filters) ──
  const filteredModels = useMemo(() => {
    let result = models;

    // Quick-search text filter
    if (filterText) {
      const q = filterText.toLowerCase();
      result = result.filter(m => {
        const text = [
          m.current_model_name, m.current_type, m.current_subtype,
          m.new_type, m.new_category
        ].join(' ').toLowerCase();
        return text.includes(q);
      });
    }

    // Column filters (same logic as Ant Design's onFilter)
    for (const [dataIndex, values] of Object.entries(columnFilters)) {
      if (values && values.length > 0) {
        result = result.filter(m => {
          const cellVal = m[dataIndex] || '';
          if (!cellVal && values.includes(EMPTY_SENTINEL)) return true;
          return values.includes(cellVal);
        });
      }
    }

    return result;
  }, [models, filterText, columnFilters]);

  // ── Stats ──
  const stats = useMemo(() => {
    let processed = 0, changed = 0, unchanged = 0, errors = 0;
    for (const m of models) {
      if (m.status === 'done') {
        processed++;
        if (m.has_changes || m._user_edited) changed++;
        else unchanged++;
      } else if (m.status === 'error') { errors++; processed++; }
    }
    return { total: models.length, processed, changed, unchanged, errors };
  }, [models]);

  // ── CSV helpers ──
  const csvEsc = (val) => {
    if (!val) return '';
    val = String(val);
    if (val.includes(',') || val.includes('"') || val.includes('\n')) {
      return '"' + val.replace(/"/g, '""') + '"';
    }
    return val;
  };

  const parseCSV = (text) => {
    const rows = [];
    let current = '';
    let inQuotes = false;
    const lines = text.split('\n');
    for (const line of lines) {
      if (inQuotes) {
        current += '\n' + line;
        if ((line.match(/"/g) || []).length % 2 === 1) {
          inQuotes = false;
          rows.push(current);
          current = '';
        }
      } else {
        if ((line.match(/"/g) || []).length % 2 === 1) {
          inQuotes = true;
          current = line;
        } else {
          rows.push(line);
        }
      }
    }
    if (current) rows.push(current);

    return rows.filter(r => r.trim()).map(row => {
      const cells = [];
      let cell = '';
      let q = false;
      for (let i = 0; i < row.length; i++) {
        const ch = row[i];
        if (q) {
          if (ch === '"' && row[i + 1] === '"') { cell += '"'; i++; }
          else if (ch === '"') { q = false; }
          else { cell += ch; }
        } else {
          if (ch === '"') { q = true; }
          else if (ch === ',') { cells.push(cell.trim()); cell = ''; }
          else { cell += ch; }
        }
      }
      cells.push(cell.trim());
      return cells;
    });
  };

  // ── CSV Export ──
  const handleExportCSV = () => {
    if (models.length === 0) {
      message.warning('No data to export');
      return;
    }
    const headers = [
      'Model ID', 'Current Model Name', 'Current Category', 'Current Type',
      'Current SubType', 'Current Code', 'Current Make', 'Current Model No',
      'New Category', 'New Type', 'New SubType', 'SubType Code', 'New Make', 'New Model No', 'New Asset Model',
      'Status', 'Has Changes'
    ];
    let csv = headers.join(',') + '\n';
    for (const m of models) {
      csv += [
        csvEsc(m.model_id), csvEsc(m.current_model_name), csvEsc(m.current_category),
        csvEsc(m.current_type), csvEsc(m.current_subtype), csvEsc(m.current_subtype_code),
        csvEsc(m.current_make), csvEsc(m.current_model_number),
        csvEsc(m.new_category), csvEsc(m.new_type), csvEsc(m.new_subtype), csvEsc(m.new_subtype_code),
        csvEsc(m.new_make), csvEsc(m.new_model_no), csvEsc(m.new_asset_model),
        m.status, m.has_changes ? 'Yes' : 'No'
      ].join(',') + '\n';
    }
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `reclassification_${orgName || 'export'}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  // ── Copy Prompt (offline reclassification route) ──
  // Builds a pasteable prompt for any chat LLM. The reply comes back either as
  // JSON (pasted straight into Paste AI Result) or as CSV (saved to a file and
  // fed through Upload CSV) — no classification server and no API key are
  // involved either way. Nothing is sent anywhere from here: the operator copies
  // the text themselves and reviews every returned row before applying.
  const handleCopyPrompt = async (format) => {
    const hasFilters = filterText || Object.keys(columnFilters).length > 0;
    const modelsForPrompt = hasFilters ? filteredModels : models;
    if (modelsForPrompt.length === 0) {
      message.warning('No models to build a prompt for');
      return;
    }

    // Org type sharpens the prompt's industry hints. Fetched at most once per
    // mount, and only when this button is used — a missing type is not fatal.
    let resolvedOrgType = orgType;
    if (!resolvedOrgType && !orgTypeFetchedRef.current) {
      orgTypeFetchedRef.current = true;
      try {
        const userData = getUserData();
        const orgObj = userData?.organisation;
        const orgId = typeof orgObj === 'object' ? orgObj?._id : orgObj;
        if (orgId) {
          const res = await apiGet(`/setting/organisation/${orgId}`, { headers: { 'Accept': '*/*' } });
          const settings = res.success ? (res.data?.data || res.data) : null;
          const typeName = typeof settings?.organisationType === 'object'
            ? settings.organisationType?.name
            : settings?.organisationType;
          if (typeName) {
            resolvedOrgType = typeName;
            setOrgType(typeName);
          }
        }
      } catch (err) {
        // Non-fatal: the prompt still works, just without the industry hint.
        console.warn('[Reclass] Could not resolve organisation type for prompt:', err);
      }
    }

    const chosenFormat = format || promptFormat;
    const parts = buildReclassificationPrompts(modelsForPrompt, {
      orgName,
      orgType: resolvedOrgType,
      format: chosenFormat,
    });
    setPromptFormat(chosenFormat);
    setPromptParts(parts);
    setPromptPartIndex(0);
    setPromptScopeLabel(
      hasFilters
        ? `${modelsForPrompt.length} filtered model(s) of ${models.length}`
        : `all ${models.length} model(s)`
    );
    setPromptModalVisible(true);
  };

  const copyTextToClipboard = (text, successMessage) => {
    const fallback = () => {
      const ta = document.createElement('textarea');
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      message.success(successMessage);
    };
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(() => message.success(successMessage)).catch(fallback);
    } else {
      fallback();
    }
  };

  const handleDownloadPrompt = (partsToWrite) => {
    const text = partsToWrite.map(p =>
      p.totalParts > 1 ? `===== PART ${p.part} OF ${p.totalParts} =====\n\n${p.text}` : p.text
    ).join('\n\n\n');
    const blob = new Blob([text], { type: 'text/plain' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `reclassification_prompt_${orgName || 'export'}.txt`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  // ── Paste AI Result (JSON) ──
  // Reads the JSON the chat LLM produced and merges it into the proposal columns.
  // Same exact-match rules and same merge helper as the CSV upload — this route
  // only removes the save-file / pick-file step.
  const handleApplyPastedJson = () => {
    const { entries, invalid, payloads, error } = parseReclassificationJson(pasteText);
    if (error) {
      setPasteError(error);
      return;
    }

    const { updated, updatedCount, notFoundCount, unchangedCount } =
      mergeClassificationEntries(models, entries);

    if (updatedCount > 0) {
      setModels(updated);
      saveReclassificationResults(orgName, updated, taxonomy, timingData);
      setSavedAt(new Date().toISOString());
    }

    // Report every bucket explicitly — an operator must be able to see that a
    // model was skipped rather than assume everything landed.
    const summary = [`${updatedCount} model(s) updated`];
    // Say how many reply blocks were read, so pasting several parts at once is
    // visibly accounted for rather than looking like a single short reply.
    if (payloads > 1) summary.push(`from ${payloads} reply blocks`);
    if (unchangedCount > 0) summary.push(`${unchangedCount} already matched`);
    if (notFoundCount > 0) summary.push(`${notFoundCount} entry(ies) not matched to any model`);
    if (invalid > 0) summary.push(`${invalid} entry(ies) skipped as incomplete`);
    const text = summary.join(', ');

    if (updatedCount > 0) {
      message.success(text);
      setPasteModalVisible(false);
      setPasteText('');
      setPasteError(null);
    } else {
      // Nothing applied — keep the modal open with the text so it can be fixed.
      setPasteError(
        `No models were updated. ${text}. Check that model_id / current_model_name were copied exactly from the prompt.`
      );
    }
  };

  // ── CSV Import ──
  const handleImportCSV = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    // Reset input so same file can be re-selected
    e.target.value = '';

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const rows = parseCSV(evt.target.result);
        if (rows.length < 2) {
          message.error('CSV file is empty or has no data rows');
          return;
        }

        // A reply pasted straight out of a chat LLM is often wrapped in a ``` fence.
        // Say so explicitly instead of failing with a confusing "missing column"
        // error — the file is NOT silently repaired, the operator fixes it.
        if (/^\s*```/.test(rows[0][0] || '')) {
          message.error('This file starts with a ``` markdown fence. Delete the ``` lines so the first line is the CSV header, then upload again.');
          return;
        }

        const headers = rows[0];
        // Map headers to indices (case-insensitive, trimmed)
        const headerMap = {};
        headers.forEach((h, i) => { headerMap[h.toLowerCase().replace(/\s+/g, '_')] = i; });

        // Determine key columns
        const modelIdIdx = headerMap['model_id'];
        const nameIdx = headerMap['current_model_name'];
        const newClassIdx = headerMap['new_category'] ?? headerMap['new_classification'];
        const newTypeIdx = headerMap['new_type'];
        const newSubIdx = headerMap['new_subtype'] ?? headerMap['new_sub_type'];
        const newSubCodeIdx = headerMap['subtype_code'] ?? headerMap['new_subtype_code'] ?? headerMap['sub_type_code'];
        const newMakeIdx = headerMap['new_make'];
        const newModelNoIdx = headerMap['new_model_no'];

        if (newClassIdx === undefined && newTypeIdx === undefined && newSubIdx === undefined && newMakeIdx === undefined) {
          message.error('CSV must have at least one of: New Category, New Type, New SubType, New Make');
          return;
        }

        // Normalise each CSV row into a merge entry. A column that is absent, or
        // present but blank, means "no proposal" and is left as undefined so the
        // shared merge keeps the current value. New Model No is the exception:
        // whenever that column exists, its value (including blank) is applied.
        const cell = (row, idx) => (idx !== undefined && row[idx] !== undefined ? row[idx].trim() : '');
        const entries = [];
        for (let r = 1; r < rows.length; r++) {
          const row = rows[r];
          if (row.length < 2) continue;
          const entry = {
            model_id: cell(row, modelIdIdx),
            current_model_name: cell(row, nameIdx),
            new_category: cell(row, newClassIdx),
            new_type: cell(row, newTypeIdx),
            new_subtype: cell(row, newSubIdx),
            new_subtype_code: cell(row, newSubCodeIdx),
            new_make: cell(row, newMakeIdx),
          };
          if (newModelNoIdx !== undefined && row[newModelNoIdx] !== undefined) {
            entry.new_model_no = row[newModelNoIdx].trim();
          }
          entries.push(entry);
        }

        const { updated, updatedCount, notFoundCount } = mergeClassificationEntries(models, entries);

        if (updatedCount > 0) {
          setModels(updated);
          saveReclassificationResults(orgName, updated, taxonomy, timingData);
          setSavedAt(new Date().toISOString());
        }

        const parts = [`${updatedCount} model(s) updated from CSV`];
        if (notFoundCount > 0) parts.push(`${notFoundCount} row(s) not matched`);
        message[updatedCount > 0 ? 'success' : 'warning'](parts.join(', '));
      } catch (err) {
        message.error('Failed to parse CSV: ' + err.message);
      }
    };
    reader.readAsText(file);
  };

  // ── Classification Map ──
  const mindmapData = useMemo(() => {
    const tree = {};
    for (const m of models) {
      if (m.status !== 'done') continue;
      const cls = m.new_category || 'Unclassified';
      const typ = m.new_type || 'Other';
      const sub = m.new_subtype || 'General';
      if (!tree[cls]) tree[cls] = {};
      if (!tree[cls][typ]) tree[cls][typ] = {};
      if (!tree[cls][typ][sub]) tree[cls][typ][sub] = [];
      tree[cls][typ][sub].push(m);
    }
    return tree;
  }, [models]);

  const mindmapStats = useMemo(() => {
    let types = 0, subtypes = 0, totalModels = 0;
    for (const cls of Object.values(mindmapData)) {
      types += Object.keys(cls).length;
      for (const typ of Object.values(cls)) {
        subtypes += Object.keys(typ).length;
        for (const sub of Object.values(typ)) totalModels += sub.length;
      }
    }
    return { classifications: Object.keys(mindmapData).length, types, subtypes, totalModels };
  }, [mindmapData]);

  // ── Table columns ──
  const columns = [
    {
      title: '#',
      key: 'index',
      width: 50,
      render: (_, record) => record._index + 1,
    },
    {
      title: 'Current Model Name',
      dataIndex: 'current_model_name',
      key: 'current_model_name',
      width: 200,
      ...getColumnFilterProps('current_model_name', 'Model Name'),
      render: (text, record) => (
        <span>
          {text || '-'}
          {record._merged_count > 1 && (
            <Tag color="purple" style={{ marginLeft: 6, fontSize: 10 }}>
              {record._merged_count} merged
            </Tag>
          )}
        </span>
      ),
    },
    {
      title: 'Current Category',
      dataIndex: 'current_category',
      key: 'current_category',
      width: 140,
      ...getColumnFilterProps('current_category', 'Category'),
      render: (text) => <span style={{ color: '#888', fontStyle: 'italic' }}>{text || '-'}</span>,
    },
    {
      title: 'Current Type',
      dataIndex: 'current_type',
      key: 'current_type',
      width: 130,
      ...getColumnFilterProps('current_type', 'Type'),
      render: (text) => <span style={{ color: '#888', fontStyle: 'italic' }}>{text || '-'}</span>,
    },
    {
      title: 'Current SubType',
      dataIndex: 'current_subtype',
      key: 'current_subtype',
      width: 150,
      ...getColumnFilterProps('current_subtype', 'SubType'),
      render: (text) => <span style={{ color: '#888', fontStyle: 'italic' }}>{text || '-'}</span>,
    },
    {
      title: 'Current Code',
      dataIndex: 'current_subtype_code',
      key: 'current_subtype_code',
      width: 80,
      ...getColumnFilterProps('current_subtype_code', 'Code'),
      render: (text) => <span style={{ color: '#888', fontStyle: 'italic' }}>{text || '-'}</span>,
    },
    {
      title: 'Current Make',
      dataIndex: 'current_make',
      key: 'current_make',
      width: 110,
      ...getColumnFilterProps('current_make', 'Make'),
      render: (text) => <span style={{ color: '#888', fontStyle: 'italic' }}>{text || '-'}</span>,
    },
    {
      title: 'Current Model No',
      dataIndex: 'current_model_number',
      key: 'current_model_number',
      width: 120,
      ...getColumnFilterProps('current_model_number', 'Model No'),
      render: (text) => <span style={{ color: '#888', fontStyle: 'italic' }}>{text || '-'}</span>,
    },
    {
      title: 'New Category',
      dataIndex: 'new_category',
      key: 'new_category',
      width: 160,
      ...getColumnFilterProps('new_category', 'Category'),
      render: (text, record) => renderEditableCell(text, record, 'new_category'),
    },
    {
      title: 'New Type',
      dataIndex: 'new_type',
      key: 'new_type',
      width: 140,
      ...getColumnFilterProps('new_type', 'Type'),
      render: (text, record) => renderEditableCell(text, record, 'new_type'),
    },
    {
      title: 'New SubType',
      dataIndex: 'new_subtype',
      key: 'new_subtype',
      width: 180,
      ...getColumnFilterProps('new_subtype', 'SubType'),
      render: (text, record) => renderEditableCell(text, record, 'new_subtype'),
    },
    {
      title: () => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <span>Code</span>
          <Tooltip title={`Auto-generate codes for ${filteredModels.length} visible rows`}>
            <Icon
              type="reload"
              onClick={(e) => {
                e.stopPropagation();
                const visibleIndices = new Set(filteredModels.map(m => m._index));
                let count = 0;
                setModels(prev => {
                  const updated = prev.map(m => {
                    if (!visibleIndices.has(m._index)) return m;
                    const autoCode = generateSubTypeCode(m.new_subtype);
                    if (!autoCode || autoCode === (m.new_subtype_code || '')) return m;
                    count++;
                    const patched = { ...m, new_subtype_code: autoCode, _user_edited: true };
                    // Recalculate has_changes with the new code
                    patched.has_changes = (
                      (patched.new_category || '').toLowerCase() !== (patched.current_category || '').toLowerCase() ||
                      (patched.new_type || '').toLowerCase() !== (patched.current_type || '').toLowerCase() ||
                      (patched.new_subtype || '').toLowerCase() !== (patched.current_subtype || '').toLowerCase() ||
                      (patched.new_make || '').toLowerCase() !== (patched.current_make || '').toLowerCase() ||
                      autoCode.toLowerCase() !== (patched.current_subtype_code || '').toLowerCase()
                    );
                    return patched;
                  });
                  saveReclassificationResults(orgName, updated, taxonomy, timingData);
                  return updated;
                });
                setTimeout(() => message.success(`Auto-generated codes for ${count} rows`), 0);
              }}
              style={{ fontSize: 12, color: '#1890ff', cursor: 'pointer' }}
            />
          </Tooltip>
        </span>
      ),
      dataIndex: 'new_subtype_code',
      key: 'new_subtype_code',
      width: 95,
      render: (text, record) => {
        const index = record._index;
        const isEditing = editingCell && editingCell.index === index && editingCell.field === 'new_subtype_code';
        if (isEditing) {
          return (
            <Input
              size="small"
              defaultValue={text || ''}
              autoFocus
              maxLength={3}
              style={{ width: 60, textTransform: 'uppercase', fontFamily: 'monospace' }}
              onPressEnter={(e) => {
                const val = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').substring(0, 3);
                updateModelField(index, 'new_subtype_code', val);
              }}
              onBlur={(e) => {
                const val = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').substring(0, 3);
                updateModelField(index, 'new_subtype_code', val);
              }}
            />
          );
        }
        const codeChanged = (text || '').toLowerCase() !== (record.current_subtype_code || '').toLowerCase();
        const autoCode = generateSubTypeCode(record.new_subtype);
        const showAutoBtn = autoCode && autoCode !== (text || '');
        return (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
            <span
              onClick={() => setEditingCell({ index, field: 'new_subtype_code' })}
              style={{
                cursor: 'pointer',
                fontFamily: 'monospace',
                fontWeight: 600,
                color: codeChanged ? '#389e0d' : (text ? '#722ed1' : '#ccc'),
                fontSize: 12,
                background: codeChanged ? '#f6ffed' : 'transparent',
                padding: codeChanged ? '1px 4px' : 0,
                borderRadius: codeChanged ? 3 : 0,
              }}
            >
              {text || '---'}
            </span>
            {showAutoBtn && (
              <Tooltip title={`Auto-generate: ${autoCode}`}>
                <Icon
                  type="reload"
                  onClick={(e) => {
                    e.stopPropagation();
                    updateModelField(index, 'new_subtype_code', autoCode);
                  }}
                  style={{ fontSize: 11, color: '#1890ff', cursor: 'pointer' }}
                />
              </Tooltip>
            )}
          </span>
        );
      },
    },
    {
      title: 'New Make',
      dataIndex: 'new_make',
      key: 'new_make',
      width: 120,
      ...getColumnFilterProps('new_make', 'Make'),
      render: (text, record) => renderEditableCell(text, record, 'new_make'),
    },
    {
      title: 'New Model No',
      dataIndex: 'new_model_no',
      key: 'new_model_no',
      width: 120,
      ...getColumnFilterProps('new_model_no', 'Model No'),
      render: (text, record) => renderEditableCell(text, record, 'new_model_no'),
    },
    {
      title: 'New Asset Model',
      dataIndex: 'new_asset_model',
      key: 'new_asset_model',
      width: 200,
      ...getColumnFilterProps('new_asset_model', 'Asset Model'),
      render: (text, record) => {
        const anyFieldChanged = record.has_changes || record._user_edited;
        return (
          <span style={{
            color: anyFieldChanged ? '#389e0d' : '#9ca3af',
            fontSize: 12,
            fontWeight: anyFieldChanged ? 500 : 400,
          }}>{text || ''}</span>
        );
      },
    },
    {
      title: () => {
        const allVisibleChangeable = filteredModels.filter(m => m.status === 'done' && (m.has_changes || m._user_edited));
        const allChecked = allVisibleChangeable.length > 0 && allVisibleChangeable.every(m => selectedForApply[m._index]);
        const someChecked = allVisibleChangeable.some(m => selectedForApply[m._index]);
        return (
          <Checkbox
            checked={allChecked}
            indeterminate={someChecked && !allChecked}
            onChange={e => toggleAllApply(e.target.checked)}
          >
            <span style={{ fontSize: 11 }}>Apply</span>
          </Checkbox>
        );
      },
      key: 'apply',
      width: 100,
      fixed: 'right',
      render: (_, record) => {
        if (record._applied) {
          return <Tag color="green"><Icon type="check" /> Applied</Tag>;
        }
        if (record.status !== 'done') {
          return <Tag color="default">{record.status === 'error' ? 'Error' : 'Pending'}</Tag>;
        }
        if (!record.has_changes && !record._user_edited) {
          return <span style={{ fontSize: 11, color: '#bbb' }}>No Changes</span>;
        }
        return (
          <Checkbox
            checked={!!selectedForApply[record._index]}
            onChange={() => toggleApply(record._index)}
          />
        );
      },
    },
  ];

  // ── Row className ──
  const rowClassName = (record) => {
    if (record._applied) return 'reclass-row-applied';
    if (selectedForApply[record._index]) return 'reclass-row-selected';
    if (record._user_edited) return 'reclass-row-edited';
    if (record.status === 'done' && record.has_changes) return 'reclass-row-changed';
    if (record.status === 'done') return 'reclass-row-unchanged';
    return '';
  };

  // ── Loading / Error states ──
  if (initializing) {
    return (
      <div style={{ padding: 24, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <Spin size="large" tip="Loading asset models from AssetPro..." />
      </div>
    );
  }

  if (initError && models.length === 0) {
    return (
      <div style={{ padding: 24, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <Card style={{ width: 500 }}>
          <Alert
            message="Failed to Load Models"
            description={initError}
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
          />
          <p style={{ color: '#888', fontSize: 13, marginBottom: 16 }}>
            Make sure you are logged in to AssetPro and the API is accessible.
          </p>
          <Button type="primary" icon="reload" onClick={handleReconnect}>
            Retry
          </Button>
        </Card>
      </div>
    );
  }

  // ── Main UI ──
  return (
    <div style={{ padding: '16px 24px', background: '#f0f2f5', minHeight: '100vh' }}>
      {/* Header bar */}
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 18, fontWeight: 600 }}>
            <Icon type="interaction" style={{ marginRight: 8 }} />
            Reclassification Engine
          </span>
          <Tag color="blue">{orgName}{orgType ? ` (${orgType})` : ''}</Tag>
        </div>
        <Button icon="reload" onClick={handleReconnect} size="small">Reload Models</Button>
      </div>

      {/* Stats row */}
      <Row gutter={12} style={{ marginBottom: 16 }}>
        <Col span={5}>
          <Card size="small">
            <Statistic title="Total Models" value={stats.total} prefix={<Icon type="database" />} valueStyle={{ color: '#1890ff' }} />
          </Card>
        </Col>
        <Col span={5}>
          <Card size="small">
            <Statistic title="Processed" value={stats.processed} prefix={<Icon type="check-circle" />} valueStyle={{ color: '#52c41a' }} />
          </Card>
        </Col>
        <Col span={5}>
          <Card size="small">
            <Statistic title="Changes Proposed" value={stats.changed} prefix={<Icon type="swap" />} valueStyle={{ color: '#faad14' }} />
          </Card>
        </Col>
        <Col span={5}>
          <Card size="small">
            <Statistic title="No Changes" value={stats.unchanged} prefix={<Icon type="minus-circle" />} valueStyle={{ color: '#999' }} />
          </Card>
        </Col>
        <Col span={4}>
          <Card size="small">
            <Statistic title="Errors" value={stats.errors} prefix={<Icon type="warning" />} valueStyle={{ color: '#ff4d4f' }} />
          </Card>
        </Col>
      </Row>

      {/* Toolbar */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <Button
            type="primary"
            icon="thunderbolt"
            onClick={handleStartReclassification}
            loading={processing}
            disabled={models.length === 0}
          >
            {stats.processed > 0 ? 'Re-run' : 'Start'} Reclassification
            {(filterText || Object.keys(columnFilters).length > 0)
              ? ` (${filteredModels.length} filtered)`
              : ''}
          </Button>
          {processing && (
            <Button
              icon="stop"
              onClick={handleStop}
              style={{ borderColor: '#ff4d4f', color: '#ff4d4f' }}
            >
              Stop
            </Button>
          )}
          <Tooltip title="Build a ready-to-paste prompt for ChatGPT / Claude / Gemini. Paste the JSON reply back with 'Paste AI Result'. No classification server or API key needed.">
            <Button
              icon="file-text"
              onClick={() => handleCopyPrompt()}
              disabled={models.length === 0}
            >
              Copy Prompt
              {(filterText || Object.keys(columnFilters).length > 0)
                ? ` (${filteredModels.length} filtered)`
                : ''}
            </Button>
          </Tooltip>
          <Tooltip title="Paste the JSON the AI replied with. Rows are matched by model id — no file to save or upload.">
            <Button
              icon="snippets"
              onClick={() => { setPasteError(null); setPasteModalVisible(true); }}
              disabled={models.length === 0}
            >
              Paste AI Result
            </Button>
          </Tooltip>
          <Button
            icon="download"
            onClick={handleExportCSV}
            disabled={models.length === 0}
          >
            Download CSV
          </Button>
          <Button
            icon="upload"
            onClick={() => fileInputRef.current && fileInputRef.current.click()}
            disabled={models.length === 0}
          >
            Upload CSV
          </Button>
          <input
            type="file"
            ref={fileInputRef}
            accept=".csv"
            style={{ display: 'none' }}
            onChange={handleImportCSV}
          />
          <Button
            icon="apartment"
            onClick={() => setMindmapVisible(true)}
            disabled={stats.processed === 0}
          >
            Category Map
          </Button>
          <Button
            icon="cluster"
            onClick={() => setFlowMapVisible(true)}
            disabled={stats.processed === 0}
            type="primary"
            ghost
          >
            Mind Map
          </Button>
          <div style={{ borderLeft: '1px solid #d9d9d9', height: 24, margin: '0 4px' }} />
          <Button
            type="primary"
            icon="sync"
            onClick={handleApplyChanges}
            loading={applying}
            disabled={selectedCount === 0}
            style={selectedCount > 0 ? { background: '#52c41a', borderColor: '#52c41a' } : {}}
          >
            Apply Changes{selectedCount > 0 ? ` (${selectedCount})` : ''}
          </Button>
          <Tooltip title="Sync all assets' sub-type to match their model's sub-type. Fixes stale references without re-running AI classification.">
            <Button
              icon="tool"
              onClick={handleRepairAssetSubTypes}
              loading={applying}
            >
              Repair Asset SubTypes
            </Button>
          </Tooltip>
          {Object.keys(columnFilters).length > 0 && (
            <Tag color="blue" closable onClose={() => setColumnFilters({})}>
              <Icon type="filter" style={{ marginRight: 4 }} />
              {Object.keys(columnFilters).length} column filter{Object.keys(columnFilters).length > 1 ? 's' : ''} active
            </Tag>
          )}
          <div style={{ flex: 1 }} />
          <Search
            placeholder="Quick search all columns..."
            allowClear
            style={{ width: 250 }}
            onChange={(e) => setFilterText(e.target.value)}
            value={filterText}
          />
        </div>

        {/* Progress when processing */}
        {processing && (
          <div style={{ marginTop: 12 }}>
            <Progress percent={30} status="active" showInfo={false} strokeColor={{ from: '#e94560', to: '#60a5fa' }} />
            <span style={{ fontSize: 12, color: '#888' }}>Running OpenAI reclassification...</span>
          </div>
        )}

        {/* Timing bar after completion */}
        {timingData && !processing && (
          <div style={{ marginTop: 12, display: 'flex', gap: 20, fontSize: 12, color: '#888' }}>
            <span>Start: <strong style={{ color: '#1890ff' }}>{timingData.startTime}</strong></span>
            <span>End: <strong style={{ color: '#1890ff' }}>{timingData.endTime}</strong></span>
            <span>Duration: <strong style={{ color: '#1890ff' }}>{(timingData.duration / 1000).toFixed(1)}s</strong></span>
            <span>Processed: <strong style={{ color: '#52c41a' }}>{timingData.processed}</strong></span>
            <span>Errors: <strong style={{ color: '#ff4d4f' }}>{timingData.errors}</strong></span>
            {timingData.stopped && <Tag color="orange">STOPPED</Tag>}
          </div>
        )}

        {/* Saved results indicator */}
        {savedAt && !processing && (
          <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#888' }}>
            <Icon type="save" style={{ color: '#52c41a' }} />
            <span>Results saved: <strong>{new Date(savedAt).toLocaleString()}</strong></span>
            {!sessionId && <Tag color="orange" style={{ marginLeft: 4 }}>Offline — server not connected</Tag>}
            <Button
              size="small"
              type="link"
              style={{ fontSize: 12, color: '#ff4d4f' }}
              onClick={() => {
                Modal.confirm({
                  title: 'Clear Saved Results?',
                  content: 'This will remove the cached reclassification data. You will need to reconnect and re-run reclassification.',
                  okText: 'Clear',
                  okType: 'danger',
                  onOk: () => {
                    clearSavedResults();
                    setSavedAt(null);
                    setModels([]);
                    setTaxonomy([]);
                    setTimingData(null);
                    message.info('Saved results cleared');
                  },
                });
              }}
            >
              Clear Saved Results
            </Button>
          </div>
        )}
      </Card>

      {/* Results table */}
      <Card
        size="small"
        title={
          <span>
            <Icon type="table" style={{ marginRight: 8 }} />
            Asset Models
            <span style={{ marginLeft: 8, fontSize: 12, color: '#888' }}>
              {filterText ? `${filteredModels.length} of ${models.length}` : `${models.length} models`}
            </span>
            <span style={{ marginLeft: 12, fontSize: 11, color: '#aaa' }}>
              <Icon type="info-circle" style={{ marginRight: 4 }} />
              Use column header filters to narrow down, then click cells to edit
            </span>
          </span>
        }
      >
        <Table
          columns={columns}
          dataSource={filteredModels}
          rowKey={(record) => record.model_id || record._index}
          loading={modelsLoading}
          scroll={{ x: 1800 }}
          pagination={{
            pageSize: 50,
            showSizeChanger: true,
            pageSizeOptions: ['20', '50', '100', '200'],
            showTotal: (total) => `Total ${total} models`
          }}
          size="small"
          rowClassName={rowClassName}
        />
      </Card>

      {/* Classification Map Modal */}
      <Modal
        title={
          <span>
            <Icon type="apartment" style={{ marginRight: 8 }} />
            Category Map — OpenAI
          </span>
        }
        visible={mindmapVisible}
        onCancel={() => setMindmapVisible(false)}
        footer={<Button onClick={() => setMindmapVisible(false)}>Close</Button>}
        width={900}
        bodyStyle={{ maxHeight: '70vh', overflow: 'auto' }}
      >
        {/* Summary stats */}
        <div style={{ display: 'flex', gap: 20, marginBottom: 16, fontSize: 13, color: '#888' }}>
          <span>Categories: <strong style={{ color: '#e94560' }}>{mindmapStats.classifications}</strong></span>
          <span>Types: <strong style={{ color: '#1890ff' }}>{mindmapStats.types}</strong></span>
          <span>SubTypes: <strong style={{ color: '#52c41a' }}>{mindmapStats.subtypes}</strong></span>
          <span>Models: <strong>{mindmapStats.totalModels}</strong></span>
        </div>

        <Collapse accordion>
          {Object.entries(mindmapData)
            .sort((a, b) => {
              const countA = Object.values(a[1]).reduce((s, t) => s + Object.values(t).reduce((s2, arr) => s2 + arr.length, 0), 0);
              const countB = Object.values(b[1]).reduce((s, t) => s + Object.values(t).reduce((s2, arr) => s2 + arr.length, 0), 0);
              return countB - countA;
            })
            .map(([clsName, typesObj]) => {
              const clsCount = Object.values(typesObj).reduce((s, t) => s + Object.values(t).reduce((s2, arr) => s2 + arr.length, 0), 0);
              return (
                <Panel
                  key={clsName}
                  header={
                    <span>
                      <strong style={{ color: '#e94560' }}>{clsName}</strong>
                      <Tag color="blue" style={{ marginLeft: 8 }}>{clsCount} models</Tag>
                    </span>
                  }
                >
                  <Collapse accordion>
                    {Object.entries(typesObj)
                      .sort((a, b) => {
                        const cA = Object.values(a[1]).reduce((s, arr) => s + arr.length, 0);
                        const cB = Object.values(b[1]).reduce((s, arr) => s + arr.length, 0);
                        return cB - cA;
                      })
                      .map(([typName, subsObj]) => {
                        const typCount = Object.values(subsObj).reduce((s, arr) => s + arr.length, 0);
                        return (
                          <Panel
                            key={typName}
                            header={
                              <span>
                                <strong style={{ color: '#1890ff' }}>{typName}</strong>
                                <Tag style={{ marginLeft: 8 }}>{typCount}</Tag>
                              </span>
                            }
                          >
                            {Object.entries(subsObj)
                              .sort((a, b) => b[1].length - a[1].length)
                              .map(([subName, modelsList]) => (
                                <div key={subName} style={{ marginBottom: 8 }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                                    <Tag color="green">{subName}</Tag>
                                    <span style={{ fontSize: 11, color: '#888' }}>
                                      {new Set(modelsList.map(m => m.new_asset_model || m.current_model_name)).size} unique model{new Set(modelsList.map(m => m.new_asset_model || m.current_model_name)).size > 1 ? 's' : ''}
                                      {modelsList.length > new Set(modelsList.map(m => m.new_asset_model || m.current_model_name)).size && (
                                        <span> ({modelsList.length} assets)</span>
                                      )}
                                    </span>
                                  </div>
                                  <div style={{ paddingLeft: 16, borderLeft: '2px solid #e8e8e8' }}>
                                    {(() => {
                                      // Group by new_asset_model to deduplicate
                                      const grouped = {};
                                      for (const mdl of modelsList) {
                                        const key = mdl.new_asset_model || mdl.current_model_name || '-';
                                        if (!grouped[key]) grouped[key] = [];
                                        grouped[key].push(mdl);
                                      }
                                      return Object.entries(grouped)
                                        .sort((a, b) => b[1].length - a[1].length)
                                        .map(([assetModel, items]) => (
                                          <div key={assetModel} style={{ fontSize: 12, color: '#666', padding: '2px 0' }}>
                                            <strong>{assetModel}</strong>
                                            {items.length > 1 && (
                                              <Tag style={{ marginLeft: 8, fontSize: 10 }}>{items.length} assets</Tag>
                                            )}
                                          </div>
                                        ));
                                    })()}
                                  </div>
                                </div>
                              ))}
                          </Panel>
                        );
                      })}
                  </Collapse>
                </Panel>
              );
            })}
        </Collapse>
      </Modal>

      {/* Interactive Mind Map Modal */}
      <Modal
        title={
          <span>
            <Icon type="cluster" style={{ marginRight: 8 }} />
            Interactive Category Mind Map
          </span>
        }
        visible={flowMapVisible}
        onCancel={() => setFlowMapVisible(false)}
        footer={<Button onClick={() => setFlowMapVisible(false)}>Close</Button>}
        width="95vw"
        bodyStyle={{ height: '80vh', padding: 0, overflow: 'hidden' }}
        style={{ top: 20 }}
        destroyOnClose
      >
        <ClassificationMindMap models={models} assetsByModelId={assetsByModelId} />
      </Modal>

      {/* Copy Prompt Modal — offline reclassification via any chat LLM */}
      <Modal
        title={
          <span>
            <Icon type="file-text" style={{ marginRight: 8 }} />
            Reclassification Prompt
            {promptParts.length > 0 && (
              <Tag color="blue" style={{ marginLeft: 8 }}>
                {promptScopeLabel}
                {promptParts.length > 1 ? ` · ${promptParts.length} parts` : ''}
              </Tag>
            )}
          </span>
        }
        visible={promptModalVisible}
        onCancel={() => setPromptModalVisible(false)}
        width={900}
        footer={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button
                type="primary"
                icon="copy"
                onClick={() => {
                  const p = promptParts[promptPartIndex];
                  if (!p) return;
                  copyTextToClipboard(
                    p.text,
                    p.totalParts > 1
                      ? `Part ${p.part} of ${p.totalParts} copied to clipboard`
                      : 'Prompt copied to clipboard'
                  );
                }}
              >
                Copy {promptParts.length > 1 ? `Part ${promptPartIndex + 1}` : 'Prompt'}
              </Button>
              <Button
                icon="download"
                onClick={() => {
                  if (promptParts.length === 0) return;
                  handleDownloadPrompt(promptParts);
                }}
              >
                Download .txt{promptParts.length > 1 ? ' (all parts)' : ''}
              </Button>
            </div>
            <Button onClick={() => setPromptModalVisible(false)}>Close</Button>
          </div>
        }
      >
        <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 13 }}>Reply format:</span>
          <Button.Group>
            <Button
              size="small"
              type={promptFormat === 'json' ? 'primary' : 'default'}
              onClick={() => handleCopyPrompt('json')}
            >
              JSON (paste back)
            </Button>
            <Button
              size="small"
              type={promptFormat === 'csv' ? 'primary' : 'default'}
              onClick={() => handleCopyPrompt('csv')}
            >
              CSV (file)
            </Button>
          </Button.Group>
        </div>
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="How to use this"
          description={
            promptFormat === 'json' ? (
              <ol style={{ paddingLeft: 18, margin: 0 }}>
                <li>Copy the prompt below and paste it into ChatGPT, Claude, Gemini or any other chat LLM.</li>
                <li>Copy the chat's <strong>JSON</strong> reply.</li>
                <li>Come back here, click <strong>Paste AI Result</strong> and paste it in — no file needed.</li>
                <li>Review every row, then <strong>Apply Changes</strong> for the ones you accept.</li>
              </ol>
            ) : (
              <ol style={{ paddingLeft: 18, margin: 0 }}>
                <li>Copy the prompt below and paste it into ChatGPT, Claude, Gemini or any other chat LLM.</li>
                <li>Save the chat's reply as a <strong>.csv</strong> file (it is already in the required format).</li>
                <li>Come back here and use <strong>Upload CSV</strong> to load the suggestions into the table.</li>
                <li>Review every row, then <strong>Apply Changes</strong> for the ones you accept.</li>
              </ol>
            )
          }
        />
        {promptParts.length > 1 && (
          <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12, color: '#888' }}>
              Split because the AI's <strong>reply</strong> would exceed the max output length
              (not the context window). Paste each part into the <strong>same chat</strong>, one
              after the other, so it reuses the Category/Type wording it already chose:
            </span>
            {promptParts.map((p, i) => (
              <Button
                key={p.part}
                size="small"
                type={i === promptPartIndex ? 'primary' : 'default'}
                onClick={() => setPromptPartIndex(i)}
              >
                Part {p.part} ({p.from}-{p.to})
              </Button>
            ))}
          </div>
        )}
        {promptParts[promptPartIndex] && (
          <>
            <div style={{ marginBottom: 6, fontSize: 12, color: '#888' }}>
              {promptParts[promptPartIndex].count} model(s) in this prompt ·{' '}
              {promptParts[promptPartIndex].text.length.toLocaleString()} characters
              {promptParts[promptPartIndex].estimate && (
                <> · roughly{' '}
                  <strong>{Math.round(promptParts[promptPartIndex].estimate.input / 1000)}k</strong> tokens in,{' '}
                  <strong>{Math.round(promptParts[promptPartIndex].estimate.output / 1000)}k</strong> out
                </>
              )}
            </div>
            <Input.TextArea
              value={promptParts[promptPartIndex].text}
              readOnly
              autosize={{ minRows: 14, maxRows: 22 }}
              style={{ fontFamily: 'monospace', fontSize: 11, background: '#fafafa' }}
              onFocus={(e) => e.target.select()}
            />
          </>
        )}
      </Modal>

      {/* Paste AI Result Modal — JSON reply straight from the chat, no file */}
      <Modal
        title={
          <span>
            <Icon type="snippets" style={{ marginRight: 8 }} />
            Paste AI Result (JSON)
          </span>
        }
        visible={pasteModalVisible}
        onCancel={() => { setPasteModalVisible(false); setPasteError(null); }}
        width={800}
        footer={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Button
              onClick={() => { setPasteText(''); setPasteError(null); }}
              disabled={!pasteText}
            >
              Clear
            </Button>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button onClick={() => { setPasteModalVisible(false); setPasteError(null); }}>Cancel</Button>
              <Button
                type="primary"
                icon="check"
                onClick={handleApplyPastedJson}
                disabled={!pasteText.trim()}
              >
                Load Suggestions
              </Button>
            </div>
          </div>
        }
      >
        <p style={{ color: '#666', marginBottom: 8 }}>
          Paste the JSON the AI replied with. Entries are matched to your models by{' '}
          <code>model_id</code>, falling back to an exact model name — anything that matches
          neither is reported, never guessed. This only fills the <strong>New *</strong> columns;
          nothing is written to AssetPro until you review and click Apply Changes.
        </p>
        <p style={{ color: '#666', marginBottom: 12, fontSize: 13 }}>
          <Icon type="info-circle" style={{ marginRight: 6, color: '#1890ff' }} />
          If the prompt was split into parts, you can paste <strong>all the replies together</strong>{' '}
          here — every JSON block is read and counted. Loading them one at a time works too;
          results accumulate.
        </p>
        {pasteError && (
          <Alert
            type="error"
            showIcon
            style={{ marginBottom: 12 }}
            message="Could not load the result"
            description={pasteError}
          />
        )}
        <Input.TextArea
          value={pasteText}
          onChange={(e) => { setPasteText(e.target.value); if (pasteError) setPasteError(null); }}
          placeholder={'{\n  "classifications": [\n    { "model_id": "...", "current_model_name": "...", "new_category": "...", "new_type": "...", "new_subtype": "...", "new_make": "...", "new_model_no": "" }\n  ]\n}'}
          autosize={{ minRows: 12, maxRows: 20 }}
          style={{ fontFamily: 'monospace', fontSize: 11 }}
        />
        {pasteText.trim() && (
          <div style={{ marginTop: 6, fontSize: 12, color: '#888' }}>
            {pasteText.length.toLocaleString()} characters pasted
          </div>
        )}
      </Modal>

      {/* Payload Preview Modal */}
      <Modal
        title={
          <span>
            <Icon type="code" style={{ marginRight: 8 }} />
            Apply Changes — API Payload Preview
            {payloadData && (
              <Tag color="blue" style={{ marginLeft: 8 }}>{payloadData.changes.length} model(s)</Tag>
            )}
          </span>
        }
        visible={payloadModalVisible}
        onCancel={() => setPayloadModalVisible(false)}
        width={800}
        bodyStyle={{ maxHeight: '70vh', overflow: 'auto' }}
        footer={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Button
              icon="copy"
              onClick={() => {
                const text = JSON.stringify(payloadData, null, 2);
                navigator.clipboard.writeText(text).then(() => {
                  message.success('Payload copied to clipboard');
                }).catch(() => {
                  // Fallback for older browsers
                  const ta = document.createElement('textarea');
                  ta.value = text;
                  document.body.appendChild(ta);
                  ta.select();
                  document.execCommand('copy');
                  document.body.removeChild(ta);
                  message.success('Payload copied to clipboard');
                });
              }}
            >
              Copy to Clipboard
            </Button>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button onClick={() => setPayloadModalVisible(false)}>Cancel</Button>
              <Button type="primary" icon="check" onClick={handleConfirmApply}>
                Confirm & Apply
              </Button>
            </div>
          </div>
        }
      >
        {payloadData && (
          <div>
            {/* Server environment warning */}
            <Alert
              message={
                <span>
                  Target Server: <strong style={{ textTransform: 'uppercase' }}>{getServerType()}</strong>
                  <span style={{ marginLeft: 8, fontSize: 12, color: '#888' }}>({getServerDomain()})</span>
                </span>
              }
              type={getServerType() === 'production' ? 'warning' : 'info'}
              showIcon
              icon={<Icon type={getServerType() === 'production' ? 'warning' : 'info-circle'} />}
              style={{ marginBottom: 12 }}
              description={getServerType() === 'production'
                ? 'You are about to apply changes to the PRODUCTION server. This will affect live data.'
                : 'You are applying changes to the DEMO server.'}
            />

            {/* Summary */}
            <div style={{ marginBottom: 16, padding: '12px 16px', background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 4 }}>
              <div style={{ display: 'flex', gap: 24, fontSize: 13 }}>
                <span>Organisation: <strong>{payloadData.organisation}</strong></span>
                <span>Models to update: <strong style={{ color: '#52c41a' }}>{payloadData.total_models}</strong></span>
                <span>With changes: <strong style={{ color: '#faad14' }}>{payloadData.changes.filter(c => c.has_changes).length}</strong></span>
              </div>
            </div>

            {/* Payload JSON */}
            <div style={{ position: 'relative' }}>
              <pre
                style={{
                  background: '#1e1e1e',
                  color: '#d4d4d4',
                  padding: 16,
                  borderRadius: 4,
                  fontSize: 12,
                  lineHeight: 1.5,
                  overflow: 'auto',
                  maxHeight: '50vh',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {JSON.stringify(payloadData, null, 2)}
              </pre>
            </div>
          </div>
        )}
      </Modal>

      {/* Apply Progress Modal */}
      <Modal
        title={
          <span>
            <Icon type="loading" spin={applying} style={{ marginRight: 8 }} />
            {applying ? 'Applying Changes...' : 'Apply Changes Complete'}
          </span>
        }
        visible={applyProgressVisible}
        onCancel={() => { if (!applying) { setApplyProgressVisible(false); setApplyResult(null); } }}
        closable={!applying}
        maskClosable={false}
        width={700}
        footer={
          applying ? null : (
            <div>
              {applyResult && (
                <Button
                  icon="download"
                  style={{ marginRight: 8 }}
                  onClick={() => {
                    const logData = {
                      ...applyResult,
                      organisation: orgName,
                      server: getServerType(),
                      serverDomain: getServerDomain(),
                      totalChanges: applyResult._totalChanges || 0,
                      summary: {
                        categories: {
                          created: applyResult.phases.categories?.created?.length || 0,
                          renamed: applyResult.phases.categories?.renamed?.length || 0,
                          reused: applyResult.phases.categories?.reused?.length || 0,
                          errors: applyResult.phases.categories?.errors?.length || 0,
                        },
                        types: {
                          created: applyResult.phases.types?.created?.length || 0,
                          renamed: applyResult.phases.types?.renamed?.length || 0,
                          reused: applyResult.phases.types?.reused?.length || 0,
                          errors: applyResult.phases.types?.errors?.length || 0,
                        },
                        subtypes: {
                          created: applyResult.phases.subtypes?.created?.length || 0,
                          renamed: applyResult.phases.subtypes?.renamed?.length || 0,
                          reused: applyResult.phases.subtypes?.reused?.length || 0,
                          errors: applyResult.phases.subtypes?.errors?.length || 0,
                        },
                        makes: {
                          created: applyResult.phases.makes?.created?.length || 0,
                          renamed: applyResult.phases.makes?.renamed?.length || 0,
                          reused: applyResult.phases.makes?.reused?.length || 0,
                          errors: applyResult.phases.makes?.errors?.length || 0,
                        },
                        models: {
                          updated: applyResult.phases.models?.updated?.length || 0,
                          reassigned: applyResult.phases.models?.reassigned?.length || 0,
                          skipped: applyResult.phases.models?.skipped?.length || 0,
                          errors: applyResult.phases.models?.errors?.length || 0,
                        },
                        assets: {
                          reassigned: applyResult.phases.assets?.reassigned?.length || 0,
                          skipped: applyResult.phases.assets?.skipped?.length || 0,
                          errors: applyResult.phases.assets?.errors?.length || 0,
                        },
                        cleanup: {
                          deleted: applyResult.phases.cleanup?.deleted?.length || 0,
                          errors: applyResult.phases.cleanup?.errors?.length || 0,
                        },
                        verification: {
                          modelsPassed: applyResult.phases.verify?.passed?.length || 0,
                          modelsFailed: applyResult.phases.verify?.failed?.length || 0,
                          assetsPassed: applyResult.phases.verify?.assetsPassed?.length || 0,
                          assetsFailed: applyResult.phases.verify?.assetsFailed?.length || 0,
                          failedModels: applyResult.phases.verify?.failed || [],
                          failedAssets: applyResult.phases.verify?.assetsFailed || [],
                          errors: applyResult.phases.verify?.errors?.length || 0,
                        },
                      },
                    };
                    const blob = new Blob([JSON.stringify(logData, null, 2)], { type: 'application/json' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
                    a.href = url;
                    a.download = `reclassification-log-${orgName}-${ts}.json`;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    URL.revokeObjectURL(url);
                    message.success('Execution log downloaded');
                  }}
                >
                  Download Log
                </Button>
              )}
              {applyResult && (
                <Button
                  icon="copy"
                  style={{ marginRight: 8 }}
                  onClick={() => {
                    navigator.clipboard.writeText(JSON.stringify(applyResult, null, 2))
                      .then(() => message.success('Log copied to clipboard'))
                      .catch(() => message.error('Failed to copy'));
                  }}
                >
                  Copy Log
                </Button>
              )}
              <Button type="primary" onClick={() => { setApplyProgressVisible(false); setApplyResult(null); }}>
                Close
              </Button>
            </div>
          )
        }
      >
        {/* Progress section */}
        {applyProgress && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <span style={{ fontWeight: 600, fontSize: 14 }}>
                {PHASE_LABELS[applyProgress.phase] || applyProgress.phase}
              </span>
              {applyStartTimeRef.current && (
                <span style={{ color: '#888', fontSize: 12 }}>
                  Elapsed: {Math.round((Date.now() - applyStartTimeRef.current) / 1000)}s
                </span>
              )}
            </div>
            {applyProgress.total > 0 && (
              <Progress
                percent={Math.round((applyProgress.current / applyProgress.total) * 100)}
                status={applying ? 'active' : 'success'}
                strokeColor={{ from: '#1890ff', to: '#52c41a' }}
              />
            )}
            <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
              {applyProgress.detail}
            </div>
          </div>
        )}

        {/* Results section */}
        {applyResult && (
          <div>
            <Alert
              message={applyResult.status === 'completed' ? 'All changes applied successfully' : applyResult.status === 'failed' ? 'Apply failed' : 'Applied with some errors'}
              type={applyResult.status === 'completed' ? 'success' : applyResult.status === 'failed' ? 'error' : 'warning'}
              showIcon
              style={{ marginBottom: 16 }}
            />

            {/* Per-phase breakdown */}
            <Collapse bordered={false} defaultActiveKey={[]}>
              {['categories', 'types', 'subtypes', 'makes'].map(phase => {
                const p = applyResult.phases[phase];
                if (!p) return null;
                const total = (p.created?.length || 0) + (p.renamed?.length || 0) + (p.reused?.length || 0);
                if (total === 0 && (p.errors?.length || 0) === 0) return null;
                return (
                  <Panel
                    key={phase}
                    header={
                      <span>
                        <strong style={{ textTransform: 'capitalize' }}>{phase}</strong>
                        {p.created?.length > 0 && <Tag color="green" style={{ marginLeft: 8 }}>{p.created.length} created</Tag>}
                        {p.renamed?.length > 0 && <Tag color="blue" style={{ marginLeft: 4 }}>{p.renamed.length} renamed</Tag>}
                        {p.reused?.length > 0 && <Tag style={{ marginLeft: 4 }}>{p.reused.length} reused</Tag>}
                        {p.errors?.length > 0 && <Tag color="red" style={{ marginLeft: 4 }}>{p.errors.length} errors</Tag>}
                      </span>
                    }
                  >
                    {p.created?.map((item, i) => (
                      <div key={`c${i}`} style={{ fontSize: 12, padding: '2px 0' }}>
                        <Icon type="plus-circle" style={{ color: '#52c41a', marginRight: 6 }} />
                        Created: <strong>{item.name}</strong>
                        {item.code && <Tag style={{ marginLeft: 6, fontSize: 10 }}>{item.code}</Tag>}
                      </div>
                    ))}
                    {p.renamed?.map((item, i) => (
                      <div key={`r${i}`} style={{ fontSize: 12, padding: '2px 0' }}>
                        <Icon type="edit" style={{ color: '#1890ff', marginRight: 6 }} />
                        Renamed: <strong>{item.name}</strong>
                      </div>
                    ))}
                    {p.errors?.map((item, i) => (
                      <div key={`e${i}`} style={{ fontSize: 12, padding: '2px 0', color: '#ff4d4f' }}>
                        <Icon type="close-circle" style={{ marginRight: 6 }} />
                        {item.name}: {item.error}
                      </div>
                    ))}
                  </Panel>
                );
              })}

              {/* Models phase */}
              {applyResult.phases.models && (
                <Panel
                  key="models"
                  header={
                    <span>
                      <strong>Models</strong>
                      {applyResult.phases.models.updated?.length > 0 && <Tag color="green" style={{ marginLeft: 8 }}>{applyResult.phases.models.updated.length} updated</Tag>}
                      {applyResult.phases.models.reassigned?.length > 0 && <Tag color="cyan" style={{ marginLeft: 4 }}>{applyResult.phases.models.reassigned.length} reassigned</Tag>}
                      {applyResult.phases.models.skipped?.length > 0 && <Tag color="orange" style={{ marginLeft: 4 }}>{applyResult.phases.models.skipped.length} skipped</Tag>}
                      {applyResult.phases.models.errors?.length > 0 && <Tag color="red" style={{ marginLeft: 4 }}>{applyResult.phases.models.errors.length} errors</Tag>}
                    </span>
                  }
                >
                  {applyResult.phases.models.reassigned?.map((item, i) => (
                    <div key={`ra${i}`} style={{ fontSize: 12, padding: '2px 0' }}>
                      <Icon type="swap" style={{ color: '#13c2c2', marginRight: 6 }} />
                      {item.name} <Icon type="arrow-right" style={{ fontSize: 10, margin: '0 4px' }} /> <strong>{item.targetName}</strong>
                      {item.assetCount > 0 && <Tag style={{ marginLeft: 6, fontSize: 10 }}>{item.assetCount} assets to move</Tag>}
                    </div>
                  ))}
                  {applyResult.phases.models.errors?.map((item, i) => (
                    <div key={`e${i}`} style={{ fontSize: 12, padding: '2px 0', color: '#ff4d4f' }}>
                      <Icon type="close-circle" style={{ marginRight: 6 }} />
                      {item.name || item.modelId}: {item.error}
                    </div>
                  ))}
                  {applyResult.phases.models.skipped?.map((item, i) => (
                    <div key={`s${i}`} style={{ fontSize: 12, padding: '2px 0', color: '#faad14' }}>
                      <Icon type="exclamation-circle" style={{ marginRight: 6 }} />
                      {item.name}: {item.reason}
                    </div>
                  ))}
                  {(applyResult.phases.models.errors?.length || 0) === 0 && (applyResult.phases.models.skipped?.length || 0) === 0 && (
                    <div style={{ fontSize: 12, color: '#52c41a' }}>
                      <Icon type="check-circle" style={{ marginRight: 6 }} />
                      All {(applyResult.phases.models.updated?.length || 0) + (applyResult.phases.models.reassigned?.length || 0)} models processed successfully
                    </div>
                  )}
                </Panel>
              )}

              {/* Asset Reassignment phase */}
              {applyResult.phases.assets && (
                (applyResult.phases.assets.reassigned?.length > 0) ||
                (applyResult.phases.assets.skipped?.length > 0) ||
                (applyResult.phases.assets.errors?.length > 0)
              ) && (
                <Panel
                  key="assets"
                  header={
                    <span>
                      <strong>Asset Reassignment</strong>
                      {applyResult.phases.assets.reassigned?.length > 0 && <Tag color="cyan" style={{ marginLeft: 8 }}>{applyResult.phases.assets.reassigned.length} moved</Tag>}
                      {applyResult.phases.assets.skipped?.length > 0 && <Tag color="orange" style={{ marginLeft: 4 }}>{applyResult.phases.assets.skipped.length} skipped</Tag>}
                      {applyResult.phases.assets.errors?.length > 0 && <Tag color="red" style={{ marginLeft: 4 }}>{applyResult.phases.assets.errors.length} errors</Tag>}
                    </span>
                  }
                >
                  {applyResult.phases.assets.reassigned?.map((item, i) => (
                    <div key={`ar${i}`} style={{ fontSize: 12, padding: '2px 0' }}>
                      <Icon type="swap" style={{ color: '#13c2c2', marginRight: 6 }} />
                      <strong>{item.assetCode}</strong>: {item.from} <Icon type="arrow-right" style={{ fontSize: 10, margin: '0 4px' }} /> {item.to}
                    </div>
                  ))}
                  {applyResult.phases.assets.skipped?.map((item, i) => (
                    <div key={`as${i}`} style={{ fontSize: 12, padding: '2px 0', color: '#faad14' }}>
                      <Icon type="exclamation-circle" style={{ marginRight: 6 }} />
                      {item.sourceModelName}: {item.reason}
                    </div>
                  ))}
                  {applyResult.phases.assets.errors?.map((item, i) => (
                    <div key={`ae${i}`} style={{ fontSize: 12, padding: '2px 0', color: '#ff4d4f' }}>
                      <Icon type="close-circle" style={{ marginRight: 6 }} />
                      {item.assetCode}: {item.error}
                    </div>
                  ))}
                  {(applyResult.phases.assets.errors?.length || 0) === 0 && (applyResult.phases.assets.skipped?.length || 0) === 0 && applyResult.phases.assets.reassigned?.length > 0 && (
                    <div style={{ fontSize: 12, color: '#52c41a', marginTop: 4 }}>
                      <Icon type="check-circle" style={{ marginRight: 6 }} />
                      All {applyResult.phases.assets.reassigned.length} assets moved successfully
                    </div>
                  )}
                </Panel>
              )}

              {/* Cleanup phase */}
              {applyResult.phases.cleanup?.deleted?.length > 0 && (
                <Panel
                  key="cleanup"
                  header={
                    <span>
                      <strong>Cleanup</strong>
                      <Tag color="orange" style={{ marginLeft: 8 }}>{applyResult.phases.cleanup.deleted.length} deleted</Tag>
                    </span>
                  }
                >
                  {applyResult.phases.cleanup.deleted.map((item, i) => (
                    <div key={i} style={{ fontSize: 12, padding: '2px 0' }}>
                      <Icon type="delete" style={{ color: '#ff4d4f', marginRight: 6 }} />
                      Deleted {item.type}: <strong>{item.name}</strong>
                    </div>
                  ))}
                </Panel>
              )}
            </Collapse>

            {/* Timing */}
            {applyResult.startedAt && applyResult.completedAt && (
              <div style={{ marginTop: 12, fontSize: 12, color: '#888', textAlign: 'right' }}>
                Total time: {((new Date(applyResult.completedAt) - new Date(applyResult.startedAt)) / 1000).toFixed(1)}s
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Row styles */}
      <style>{`
        .reclass-row-changed { background-color: rgba(52, 211, 153, 0.08) !important; }
        .reclass-row-unchanged { opacity: 0.7; }
        .reclass-row-edited { background-color: rgba(251, 191, 36, 0.1) !important; }
        .reclass-row-selected { background-color: rgba(24, 144, 255, 0.08) !important; }
        .reclass-row-applied { background-color: rgba(82, 196, 26, 0.12) !important; }
      `}</style>
    </div>
  );
}

export default AssetReclassification;
